import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getGeocodeConnector } from '../../../shared/maps';
import {
  makeStyles,
  shorthands,
  tokens,
  Input,
  Button,
  Text,
  Spinner,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Label,
  Dropdown,
  Option,
  Checkbox,
  Link,
  Toast,
  ToastTitle,
  ToastBody,
  Toaster,
  Tooltip,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  useToastController,
  useId,
} from '@fluentui/react-components';
import {
  ChevronRight16Regular,
  Warning20Regular,
  LockClosed20Regular,
  Edit20Regular,
  Archive20Regular,
  ArrowUndo20Regular,
  DismissCircle20Regular,
  MoreHorizontal20Regular,
  Mail20Regular,
  Box24Regular,
  Money16Regular,
  PanelRight24Regular,
  Info16Regular,
  CalendarClock20Regular,
} from '@fluentui/react-icons';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import AddressFields from '../components/AddressFields';
import EmailHistoryDialog, { type EmailHistoryEntry, type EmailDeliveryStatus } from '../components/EmailHistoryDialog';
import type { DocumentDetails } from '../services/documentService';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import Header from '../components/Header';
import CustomerDrawer from '../components/CustomerDrawer';
import StartItemsDialog from '../components/StartItemsDialog';
import OrderRightSidebar from '../components/OrderRightSidebar';
import OrderCustomerNotesSection from '../components/OrderCustomerNotesSection';
import { OrderDetailsSkeleton, ProductsSkeleton } from '../components/OrderDetailsSkeleton';
import type { TonnageOverageSummary } from '../components/TonnageSection';
import { DEFAULT_PAYMENT_TERMS } from '../components/DocumentPreview';
import {
  CustomerSection,
  BillingRentalSection,
  LocationSection,
  ProductsSection,
  type OrderLineRow,
  type OrderLineStockUnit,
} from '../components/orderForm';
import OrderSections from '../components/OrderSections';
import PhaseProducts from '../components/PhaseProducts';
import type { OrderSection } from '../services/orderService';
import { addSection, assignLineSection, deleteSection } from '../services/sectionService';
import type { MovePhaseOption } from '../components/MovePhaseMenu';
import { fetchOrderLocalRow } from '../services/orderService';
import { useProductSelection, type LineAddStartInfo, type LineAddedInfo } from '../hooks/useProductSelection';
import { useProductCatalog } from '../hooks/useProductCatalog';
import { useAutoSave } from '../hooks/useAutoSave';
import { useIsMobile } from '../hooks/useIsMobile';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryClient';
import {
  fetchCustomerLocations,
  addCustomerLocation,
  type CustomerLocation as SavedCustomerLocation,
} from '../services/customerLocationService';
import { fetchCustomerById } from '../services/customerService';
import {
  resolveWithholdingAmount,
} from '../lib/withholding';
import SaveStatusBadge from '../components/customerForm/SaveStatusBadge';
import OrderStatusPill from '../components/OrderStatusPill';
import PaymentStatusPill from '../components/PaymentStatusPill';
import { apiFetch, apiFetchWithRetry } from '../lib/apiFetch';
import { savePhaseSchedule } from '../services/projectService';
import { applyPhaseEdit, addDays, type EditablePhase, type CascadeMode } from '../lib/phaseDates';
import { usePhaseCascadePref } from '../hooks/usePhaseCascadePref';
import PhaseCascadeChoice from '../components/PhaseCascadeChoice';
import {
  readOrderDraftScratch,
  writeOrderDraftScratch,
  clearOrderDraftScratch,
} from '../lib/orderDraftScratchpad';
import { SectionShell } from '../components/orderForm';

const useStyles = makeStyles({
  // Header action cluster (payment pill + lifecycle button + ⋯ menu). Fluent
  // v9 buttons render with the default arrow cursor — these are clearly
  // clickable, so opt every button in the cluster into a pointer cursor.
  actionCluster: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '& button': {
      cursor: 'pointer',
    },
  },
  // Saved-address picker in the billing dialog: the selected value is a full
  // one-line address that overflows the dropdown trigger. Truncate it with an
  // ellipsis instead of letting the button wrap to multiple lines.
  savedAddressDropdown: {
    '& button': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  // Single elevated workspace card containing the inline Header + body row.
  // Mirrors /customers and /customers/:id so /orders/new and /orders/:id
  // read as one continuous surface.
  workspaceCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    flexGrow: 1,
    // Pin the card to EXACTLY the viewport height (minus the 48px global
    // header + 8px top/bottom margins) — not just a min-height floor. The
    // body row scrolls its main column internally while the right sidebar
    // stays pinned (see `body`/`mainColumn`). A min-height floor let the card
    // grow PAST the viewport on long orders, which moved the scroll up to
    // .main-content and carried the sidebar out of view. A fixed height both
    // guarantees the fill (no gray backdrop gap when content is short) and
    // caps it so the internal scroll engages and the sidebar holds its place.
    // flexShrink:0 stops flexbox from squeezing it back under that height.
    height: 'calc(100vh - 64px)',
    flexShrink: 0,
    ...shorthands.margin('8px'),
    ...shorthands.overflow('hidden'),
    // Mobile: full-bleed work surface — drop the margin gutter and rounded
    // corners so the content uses every pixel of width.
    '@media (max-width: 768px)': {
      ...shorthands.margin('0'),
      ...shorthands.borderRadius('0'),
    },
  },
  // Thin passthrough wrapper around the inline Header so it can cast a scroll
  // shadow onto the content below (the shared Header takes no className). Sits
  // above the scrolling body in its own stacking context so the shadow lands on
  // top of the content rather than behind it.
  headerWrap: {
    position: 'relative',
    zIndex: 1,
    flexShrink: 0,
    transitionProperty: 'box-shadow',
    transitionDuration: '150ms',
    transitionTimingFunction: 'ease',
  },
  // Applied once the main column is scrolled off the top — a soft, bottom-only
  // shadow so the Header reads as elevated above the scrolled content.
  headerWrapScrolled: {
    boxShadow: '0 6px 8px -6px rgba(0, 0, 0, 0.16)',
  },
  // Body hosts the form sections column + right sidebar. The borderTop
  // separates the inline Header from this row.
  body: {
    display: 'flex',
    alignItems: 'stretch',
    flexGrow: 1,
    minHeight: 0,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.overflow('hidden'),
  },
  // Main column scrolls independently of the right sidebar — long
  // forms (location map, products list) shouldn't push the sidebar.
  // Grey backdrop so the white section cards inside read as inset
  // blocks rather than white-on-white.
  mainColumn: {
    flexGrow: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  // Centered work area — readable max-width so form fields don't stretch
  // edge-to-edge on wide monitors. Bumped to 1200px to fit the 2-col
  // layout (customer/location stacked left, billing right).
  workArea: {
    width: '100%',
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    ...shorthands.padding('16px'),
  },
  // Accidental-edit guard banner shown on dispatched/completed orders. White
  // surface + Pumpkin accent border/icon, neutral text (accent-not-fill warning
  // language), with an explicit "Edit order" unlock on the right.
  lockBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('10px', '14px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorPalettePumpkinBorderActive),
    ...shorthands.borderRadius('4px'),
  },
  lockBannerText: {
    flexGrow: 1,
    color: tokens.colorNeutralForeground1,
  },
  // Soft, non-blocking warning shown when the customer requires a PO and the
  // order's PO field is empty. Same white-surface + Pumpkin-accent language as
  // the lock banner (accent, not fill). See #1178.
  poWarnBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ...shorthands.padding('8px', '12px'),
    marginTop: '8px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorPalettePumpkinBorderActive),
    ...shorthands.borderRadius('4px'),
  },
  // Top row: two columns. Left holds Customer + Location stacked; right
  // holds Billing & rental period. On narrow viewports collapse to a
  // single column.
  topRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
    },
  },
  topLeftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minWidth: 0,
  },
  topRightColumn: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    // Stretch the billing section to fill the column height so it
    // matches the combined Customer+Location stack on the left.
    '& > *': {
      flexGrow: 1,
    },
  },
  // Title slot inside the Header's leftSection. Stretches to fill the
  // available width so the breadcrumb + order-status pill cluster on the
  // left and the payment-status pill sits on the right, adjacent to the
  // toolbar action buttons in the Header's rightSection.
  headerTitleRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    columnGap: '8px',
  },
  // Left cluster: breadcrumb + labeled Order pill + save-status badge.
  headerTitleLeft: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    minWidth: 0,
  },
  // Inline wrapper so a status pill can be a Tooltip target (the tooltip
  // names the dimension; the dimension icon sits inside the pill itself).
  statusTipTarget: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  contextMenu: {
    position: 'fixed',
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    minWidth: '180px',
    zIndex: 1000,
    ...shorthands.padding('4px', '0'),
  },
  contextMenuItem: {
    ...shorthands.padding('8px', '16px'),
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
});

interface Customer {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  municipality?: string;
  zipCode?: string;
  country?: string;
}

interface CustomerField {
  identifier: string;
  value: string;
  label?: string;
}

// Parent-project context for a phase order (subset of GET /orders/<id>'s
// `project` block). Drives the read-only location + the date-chaining UI.
interface OrderProjectCtx {
  id: string;
  projectNumber: number;
  poNumber: string | null;
  deliveryAddress: string | null;
  deliveryLocationName: string | null;
  deliveryLatitude: number | null;
  deliveryLongitude: number | null;
  phaseNumber: number | null;
  phaseCount: number;
}

// Build the editable schedule from GET /orders/<id>'s snake_case `project.phases`.
const phaseScheduleFromOrderData = (proj: any): EditablePhase[] =>
  (proj?.phases || []).map((p: any) => ({
    id: String(p.id),
    phaseNumber: p.phase_number,
    startDate: p.starts_at ? String(p.starts_at).slice(0, 10) : null,
    endDate: p.stops_at ? String(p.stops_at).slice(0, 10) : null,
    promoted: !!p.booqable_id,
  }));

// Map every item_id (product-group id AND each variation id) to its product
// metadata. An order line's item_id can reference either a simple product
// group or a specific variation, so we index both. Lets a line tell whether
// it's a trackable rental (show stock-item picker + rental duration) or a
// non-trackable sales item like the bulldog bag (hide both).
const buildProductMeta = (
  groups: Array<{ id?: string; trackable?: boolean; product_type?: string; variations?: Array<{ id?: string }> }>
): Record<string, { trackable?: boolean; productType?: string }> => {
  const map: Record<string, { trackable?: boolean; productType?: string }> = {};
  for (const pg of groups || []) {
    const meta = { trackable: pg.trackable, productType: pg.product_type };
    if (pg.id) map[pg.id] = meta;
    for (const v of pg.variations || []) {
      if (v.id) map[v.id] = meta;
    }
  }
  return map;
};

// Haversine distance between two coordinates, in meters. Pure — hoisted to
// module scope so it isn't reallocated each render and can be referenced from
// memoized callbacks without becoming a dependency.
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371000; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
};

// Derive the order's effective payment_status from its money attrs, correcting
// two Booqable quirks: a zero-total draft reports "payment_paid" (0 >= 0), and a
// fully-paid order can report "partially_paid" from float drift (#299/#300).
// Shared by the ['order-payment-status'] query AND the main order refetch, which
// seeds this query's cache from its own response — the heavy include already
// returns these fields, so the pill no longer needs a separate fetch after a
// line add/remove. Compares paid against the TAX-INCLUSIVE total (a payment
// settles the taxed invoice balance; grand_total_in_cents is pre-tax in v4).
const derivePaymentStatus = (attrs: any): string | null => {
  const grandTotalCents = Number(
    attrs?.grand_total_with_tax_in_cents ?? attrs?.grand_total_in_cents ?? 0,
  );
  const paidCents = Number(attrs?.amount_paid_in_cents || 0);
  if (grandTotalCents <= 0) return null;
  if (paidCents >= grandTotalCents) {
    return paidCents > grandTotalCents ? 'overpaid' : 'payment_paid';
  }
  if (paidCents > 0) return 'partially_paid';
  return 'payment_due';
};

const KNOWN_EMAIL_STATUSES: EmailDeliveryStatus[] = [
  'sent', 'delivered', 'opened', 'bounced', 'complained', 'failed',
];

export default function OrderDetails() {
  const styles = useStyles();
  const navigate = useNavigate();
  // True once the main content column is scrolled off the top — drives the
  // Header's scroll shadow so it reads as elevated above the scrolled content.
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const { orderId } = useParams<{ orderId: string }>();
  const isNewOrder = orderId === 'new' || !orderId;
  const [searchParams, setSearchParams] = useSearchParams();
  const toasterId = useId('order-toaster');
  const { dispatchToast } = useToastController(toasterId);

  const isMobile = useIsMobile();
  // Mobile-only: the right sidebar is a slide-in overlay (the 260px rail eats
  // most of a phone viewport), defaulting closed so the form gets full width.
  // Desktop ignores this — the sidebar always sits inline.
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Tracks whether the scratchpad-restore effect has run, so the persist
  // effect doesn't write empty defaults over a stored scratchpad before
  // restore happens.
  const scratchRestoredRef = useRef(false);

  // Bumped by useProductSelection's onLineAdded callback to force the order-fetch
  // useEffect to re-run after a line is added. The page uses raw apiFetch() rather than
  // React Query, so cache invalidation isn't available — this counter is the manual
  // refetch trigger. Worth migrating to React Query as a follow-up.
  const [lineRefetchTrigger, setLineRefetchTrigger] = useState(0);

  // Line IDs the user has removed during this session. Booqable's get-order
  // is eventually consistent: the refetch fired right after DELETE /lines/:id
  // sometimes still returns the just-deleted line in `included`, causing it
  // to reappear in the table. We filter these IDs out of every refetch
  // response so the UI never shows a line the user has explicitly deleted.
  const deletedLineIdsRef = useRef<Set<string>>(new Set());

  // Per-unit price overrides the user just committed, keyed by line ID. Same
  // eventual-consistency hazard as deletes: the reconcile fired right after
  // PUT /lines/:id/price can still echo the pre-override price_each_in_cents
  // for ~1s, which would clobber the optimistic override and make a just-typed
  // price randomly snap back to the tier default. We re-apply the pending value
  // on every reconcile and drop the entry once Booqable's echo matches it.
  const pendingLinePriceRef = useRef<Map<string, number>>(new Map());

  // Project id when this order is a phase child, mirrored into a ref so
  // refreshOrderInvoices (defined above the orderProject state) can invalidate
  // the project query without a TDZ in its deps. Kept in lockstep with
  // setOrderProject. Lets a line-total change here refresh the phase card's
  // total on the project page instead of waiting on a manual refresh.
  const orderProjectIdRef = useRef<string | null>(null);

  // Hoisted above useProductSelection / handleUpdateLine* because their
  // useCallback deps reference queryClient; the deps array is evaluated at
  // render time, so the binding must be initialized first or it hits TDZ.
  const queryClient = useQueryClient();

  // Declared above the product-selection hook because its add callbacks read
  // this state.
  const [orderLines, setOrderLines] = useState<OrderLineRow[]>([]);
  // In-order phases (sections) on this order + the Booqable-line-id → section-id
  // tag map, both seeded from the /orders/<id> payload. Drive the OrderSections
  // panel; empty for a plain order.
  const [orderSections, setOrderSections] = useState<OrderSection[]>([]);
  const [lineSectionMap, setLineSectionMap] = useState<Record<string, string | null>>({});
  // Live umbrella (min delivery → max pickup over dated phases, YYYY-MM-DD),
  // reported by OrderSections on each schedule change — including the optimistic
  // edit before the save round-trip. Drives the read-only top date display so it
  // tracks phase edits instantly (the reconcile refetch skips re-applying dates).
  const [phaseUmbrella, setPhaseUmbrella] = useState<{ start: string | null; end: string | null } | null>(null);

  // Optimistic line lifecycle for the product picker. onLineAddStart drops a
  // placeholder row the instant an item is picked (so it appears without
  // waiting on the multi-hop add-line POST); onLineAdded swaps it for the real
  // line; onLineAddFailed removes it if the POST fails. Stable identities keep
  // handleProductSelect — and thus the memoized ProductsSection — from churning
  // on every parent render.
  const handleLineAddStart = useCallback((info: LineAddStartInfo) => {
    const estUnit = info.productGroup.price != null ? Math.round(info.productGroup.price * 100) : 0;
    // Window-available count for this product, carried on the picker's
    // ProductGroup. When it's 0 the add still proceeds (warn-and-allow, #2) —
    // surface a white/orange MessageBar so staff know the window has no free units.
    // Services have no tracked stock (availableCount is always 0), so they can
    // never be short — skip the notice for them or every service line would
    // falsely report a shortage.
    const windowAvailable = info.productGroup.availableCount;
    const isService = info.productGroup.product_type === 'service';
    if (windowAvailable === 0 && !isService) {
      setShortageNotice({
        message: 'Added with shortage — no units available for this window.',
      });
    }
    setOrderLines((prev) => [
      ...prev,
      {
        id: info.tempId,
        itemId: info.productGroup.id,
        name: info.productGroup.name,
        quantity: info.quantity,
        priceInCents: estUnit,
        totalInCents: estUnit * info.quantity,
        stockCount: info.productGroup.totalCount,
        availableCount: windowAvailable,
        pending: true,
      },
    ]);
    // Tag the placeholder to its phase (null = Unscheduled / plain order) so the
    // section filter drops it into the right card the instant it appears — the
    // same map the settled line will live in once handleLineAdded rekeys it.
    setLineSectionMap((prev) => ({ ...prev, [info.tempId]: info.sectionId ?? null }));
  }, []);

  const handleLineAdded = useCallback((info: LineAddedInfo) => {
    // Reconcile the placeholder in place with the authoritative price + real
    // line id. The refetch below pulls full totals/tax from Booqable.
    // Prefer `linePriceInCents` — the definitive per-unit price the backend read
    // off the just-created line (TWM's override, OR the product-default price
    // Booqable booked it at). It's populated even for product_default / flat-fee
    // lines, so the row settles here instead of spinning until the refetch.
    const unitPrice =
      info.linePriceInCents ??
      info.appliedPriceInCents ??
      (info.productGroup.price != null ? Math.round(info.productGroup.price * 100) : 0);
    // With linePriceInCents now carrying the booked default, a confident price
    // is known the instant the add resolves for virtually every line — so the
    // row drops "Adding…" immediately and the background reconcile + refetch
    // (fired below) only reconcile authoritative totals/tax a few seconds later,
    // off the critical path. The one case that legitimately stays $0 (a manual-
    // quote / unresolved line the staff prices inline) keeps the placeholder
    // until the refetch rather than flashing a misleading $0.00.
    const haveConfidentPrice = unitPrice > 0;
    setOrderLines((prev) =>
      prev.map((l) =>
        l.id === info.tempId
          ? {
              ...l,
              id: info.lineId ?? l.id,
              priceInCents: unitPrice,
              totalInCents: unitPrice * l.quantity,
              stockCount: info.productGroup.totalCount,
              availableCount: info.productGroup.availableCount,
              pricingSource: info.pricingSource,
              pending: !haveConfidentPrice,
            }
          : l,
      ),
    );
    // Rekey the section tag from the temp id to the real line id so the row
    // stays in its phase card after it settles (no-op until a real id lands).
    if (info.lineId && info.lineId !== info.tempId) {
      setLineSectionMap((prev) => {
        if (!(info.tempId in prev)) return prev;
        const { [info.tempId]: sectionId, ...rest } = prev;
        return { ...rest, [info.lineId as string]: sectionId };
      });
    }
    // The line's identity + price are already reconciled above — that's all the
    // add-line POST now does. Everything else (stock units / SKU pills, the
    // Extra Day line, the pro forma invoice, the mirror totals) was split off
    // the POST into a background reconcile so the row stops spinning the moment
    // the line lands (see reconcile-after-line in app.py). Trigger the order
    // refetch only ONCE that reconcile resolves, so the refetch sees the
    // specified units + re-synced extra-day line instead of racing them — and
    // refresh the sidebar's Invoices section + payment pill at the same time.
    if (orderId && !isNewOrder) {
      apiFetch(`/booqable/orders/${encodeURIComponent(orderId)}/reconcile-after-line`, {
        method: 'POST',
      })
        .catch((err) => console.error('post-line reconcile failed:', err))
        .finally(() => {
          // The refetch reseeds the payment pill from its own response (see
          // derivePaymentStatus seed in fetchOrderData), so no separate
          // order-payment-status fetch is needed here. Documents is its own
          // list (not in the include), so it still needs an invalidate.
          setLineRefetchTrigger((c) => c + 1);
          queryClient.invalidateQueries({ queryKey: ['order-documents', orderId] });
        });
    } else {
      // No reconcile endpoint for an un-promoted/new order — refetch directly so
      // the optimistic row still reconciles against Booqable.
      setLineRefetchTrigger((c) => c + 1);
    }
  }, [orderId, isNewOrder, queryClient]);

  const handleLineAddFailed = useCallback((info: { tempId: string }) => {
    setOrderLines((prev) => prev.filter((l) => l.id !== info.tempId));
    setLineSectionMap((prev) => {
      if (!(info.tempId in prev)) return prev;
      const { [info.tempId]: _drop, ...rest } = prev;
      return rest;
    });
  }, []);

  // Refresh the sidebar's Invoices section + payment "Due" row after a
  // line-total change (recalc, qty edit, overage line). The Due figure reads
  // from the ['order-documents'] query, which is NOT part of the order refetch
  // (setLineRefetchTrigger), so it stayed stale until a manual page refresh.
  // Booqable regenerates the payment_due proforma when line totals change and
  // that regeneration is eventually consistent, so re-invalidate once more
  // after a short delay as a safety net (mirrors the remove-line path).
  const refreshOrderInvoices = useCallback(() => {
    if (!orderId || isNewOrder) return;
    queryClient.invalidateQueries({ queryKey: ['order-documents', orderId] });
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['order-documents', orderId] });
    }, 1500);
    // When this order is a project phase, its line total feeds the phase card's
    // total + item count on the project page. That query isn't part of the order
    // refetch, so it stayed stale until a manual refresh — invalidate it (and the
    // projects list) so the card tracks the change.
    if (orderProjectIdRef.current) {
      const pid = orderProjectIdRef.current;
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(pid) });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(pid) });
      }, 1500);
    }
  }, [orderId, isNewOrder, queryClient]);

  // Optimistically reflect the tonnage overage line from the save response so it
  // appears the instant the POST resolves, instead of waiting on the full order
  // refetch. Tonnage is now per weighed line, so an order can carry several
  // overage lines — we must touch ONLY the one this save changed. A re-save
  // deletes the old Booqable line and mints a new id, so the summary carries
  // `previous_overage_line_id` to drop exactly the stale row (matching by the
  // displayOverride tag would wrongly clear other lines' overage). The
  // background refetch still reconciles authoritative totals/tax + the note.
  const handleTonnageLinesChanged = useCallback((summary?: TonnageOverageSummary) => {
    const prevId = summary?.previous_overage_line_id || null;
    const dropPrev = (rows: OrderLineRow[]) =>
      prevId ? rows.filter((l) => l.id !== prevId) : rows;
    if (summary?.overage_line_action === 'created' && summary.overage_line_id) {
      // Mirror Python's :g — trims trailing zeros (2.0 -> "2", 1.5 -> "1.5") so
      // the title matches the server-rendered one and the refetch swap is silent.
      const fmtG = (n: number) => String(Number(n));
      const cents = Number(summary.overage_in_cents) || 0;
      const optimistic: OrderLineRow = {
        id: summary.overage_line_id,
        name: `Heavy material — ${fmtG(summary.overage_tons)} tons over ${fmtG(summary.included_tons)}-ton allotment`,
        quantity: 1,
        priceInCents: cents,
        totalInCents: cents,
        kind: 'custom',
        displayOverride: { showQty: false },
      };
      setOrderLines((prev) => {
        const cleaned = dropPrev(prev);
        const idx = cleaned.findIndex((l) => l.id === optimistic.id);
        if (idx === -1) return [...cleaned, optimistic];
        const next = cleaned.slice();
        next[idx] = { ...next[idx], ...optimistic };
        return next;
      });
    } else if (summary?.overage_line_action === 'removed') {
      setOrderLines((prev) => dropPrev(prev));
    }
    setLineRefetchTrigger((c) => c + 1);
    refreshOrderInvoices();
  }, [refreshOrderInvoices]);

  // Tier-pricing resolver for line-item adds — see planning/booqable-cutover-playbook.md
  // "Pricing Authority". Returns customer's tier metadata + a tier-aware add handler.
  const {
    handleProductSelect,
    tier,
    callForPrice,
    municipalityName,
    serviceAddressMissing,
    serviceAddressCaptured,
    tierResolving,
    refetchTier,
    clearTier,
    isAdding: isAddingLine,
    lastError: pricingError,
  } = useProductSelection({
    orderId: isNewOrder ? null : orderId,
    onLineAddStart: handleLineAddStart,
    onLineAdded: handleLineAdded,
    onLineAddFailed: handleLineAddFailed,
  });

  // Optimistically reflect the new qty in the row while the PATCH is in
  // flight, then refetch to pull authoritative price/tax from Booqable.
  const handleUpdateLineQuantity = useCallback(
    async (lineId: string, quantity: number) => {
      setOrderLines((prev) =>
        prev.map((l) =>
          l.id === lineId
            ? { ...l, quantity, totalInCents: (l.priceInCents || 0) * quantity }
            : l
        )
      );
      // Recompute the summary from the live lines until the refetch reconciles.
      setOptimisticTotals(true);
      try {
        const res = await apiFetch(`/booqable/lines/${encodeURIComponent(lineId)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ quantity }),
        });
        if (!res.ok) {
          console.error('[update-line] failed:', res.status, await res.text());
        }
      } catch (err) {
        console.error('[update-line] error:', err);
      } finally {
        // payment-status pill is reseeded by the refetch (see derivePaymentStatus
        // seed in fetchOrderData) — no separate ?fields[orders]=… fetch needed.
        setLineRefetchTrigger((c) => c + 1);
        // Quantity change moves stock for the window — refresh the catalog so
        // the picker's availability count tracks it (overbook guard).
        queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
        // ...and the new line total changes the payment Due in the sidebar.
        refreshOrderInvoices();
      }
    },
    [orderId, isNewOrder, queryClient, refreshOrderInvoices]
  );

  const [isReserving, setIsReserving] = useState(false);
  // Cutover: the booking-confirmation email is a manual push (the order.reserved
  // webhook's auto-send is gated off while we straddle the old system), so staff
  // send it by hand from the ⋯ menu. Tracks the in-flight send to avoid a
  // double-fire from a quick double-click.
  const [isSendingConfirmation, setIsSendingConfirmation] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Reverts (un-archive / step back to an earlier status) get their own flag so
  // the forward primary button (Archive on stopped, Complete on started, …)
  // doesn't relabel to "Archiving…/Completing…" while a revert is in flight.
  const [isReverting, setIsReverting] = useState(false);

  // Cancel + revert live behind confirmation dialogs. cancel is a side exit
  // valid only from draft/reserved; revert un-archives an order back to an
  // earlier status. revertTarget holds the chosen target while its confirm
  // dialog is open.
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [revertTarget, setRevertTarget] = useState<'draft' | 'reserved' | 'started' | 'stopped' | null>(null);

  // Generic status transition: hits a /booqable/orders/<id>/<action>
  // endpoint and refetches. Used for start/stop/archive (cancel lives on
  // the Orders list). Same shape as handleReserve.
  const runOrderTransition = useCallback(
    async (action: 'start' | 'stop' | 'archive', failTitle: string) => {
      if (!orderId || isNewOrder || isTransitioning) return;
      setIsTransitioning(true);
      try {
        const res = await apiFetch(
          `/booqable/orders/${encodeURIComponent(orderId)}/${action}`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' } },
        );
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          const msg = body?.error || `${failTitle} (HTTP ${res.status})`;
          dispatchToast(
            <Toast>
              <ToastTitle>{failTitle}</ToastTitle>
              <ToastBody>{msg}</ToastBody>
            </Toast>,
            { intent: 'error' },
          );
          return;
        }
        // Optimistically flip the status so the action button + status pill
        // change the instant the POST succeeds, instead of waiting on the
        // eventually-consistent order refetch below. The transition map
        // matches the strings attrs.status returns, so the refetch reconciles
        // without a flicker. (Top-level order.status is fresh on GET — only
        // sideloaded child rows lag, which is why this is safe here.)
        const nextStatus = { start: 'started', stop: 'stopped', archive: 'archived' }[action];
        setOrderStatus(nextStatus);
        setLineRefetchTrigger((c) => c + 1);
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.concept });
        // start/stop/archive all change stock availability — refetch the
        // catalog so the product-picker availability counts stay current.
        queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      } catch (err) {
        const msg = err instanceof Error ? err.message : failTitle;
        dispatchToast(
          <Toast>
            <ToastTitle>{failTitle}</ToastTitle>
            <ToastBody>{msg}</ToastBody>
          </Toast>,
          { intent: 'error' },
        );
      } finally {
        setIsTransitioning(false);
      }
    },
    [orderId, isNewOrder, isTransitioning, queryClient, dispatchToast],
  );

  // Start is always confirmed through the items dialog (which items are going
  // out). The button opens it; the dialog's confirm runs the actual transition.
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const handleStart = useCallback(() => setStartDialogOpen(true), []);

  // Run the start the dialog asked for. A strict subset is a partial start
  // (POST line_ids → order stays reserved with those plannings out); selecting
  // every remaining item omits line_ids so the all-items path forces 'started'.
  const confirmStart = useCallback(
    async (selectedLineIds: string[], isAllRemaining: boolean) => {
      if (!orderId || isNewOrder || isTransitioning) return;
      setIsTransitioning(true);
      try {
        const res = await apiFetch(
          `/booqable/orders/${encodeURIComponent(orderId)}/start`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(isAllRemaining ? {} : { line_ids: selectedLineIds }),
          },
        );
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          dispatchToast(
            <Toast>
              <ToastTitle>Could not start order</ToastTitle>
              <ToastBody>{body?.error || `Could not start order (HTTP ${res.status})`}</ToastBody>
            </Toast>,
            { intent: 'error' },
          );
          return;
        }
        // Mirror whatever status the backend settled on — 'started' when the
        // order is fully out, 'reserved' when only some items went (partial).
        setOrderStatus(body?.status || 'started');
        setStartDialogOpen(false);
        setLineRefetchTrigger((c) => c + 1);
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.concept });
        queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Could not start order';
        dispatchToast(
          <Toast>
            <ToastTitle>Could not start order</ToastTitle>
            <ToastBody>{msg}</ToastBody>
          </Toast>,
          { intent: 'error' },
        );
      } finally {
        setIsTransitioning(false);
      }
    },
    [orderId, isNewOrder, isTransitioning, queryClient, dispatchToast],
  );
  const handleStop = useCallback(() => runOrderTransition('stop', 'Could not stop order'), [runOrderTransition]);
  const handleArchive = useCallback(() => runOrderTransition('archive', 'Could not archive order'), [runOrderTransition]);

  const handleReserve = useCallback(async () => {
    if (!orderId || isNewOrder || isReserving) return;
    setIsReserving(true);
    try {
      const res = await apiFetch(`/booqable/orders/${encodeURIComponent(orderId)}/reserve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = body?.error || `Reserve failed (HTTP ${res.status})`;
        console.error('[reserve] failed:', msg, body);
        // Toast, not setError — setError swaps the page out for a fatal
        // error screen, which is overkill for a button-click failure.
        dispatchToast(
          <Toast>
            <ToastTitle>Could not reserve order</ToastTitle>
            <ToastBody>{msg}</ToastBody>
          </Toast>,
          { intent: 'error' }
        );
        return;
      }
      // Optimistically flip to 'reserved' so the Reserve button becomes
      // Start (and the pill turns blue) immediately; the refetch reconciles.
      setOrderStatus('reserved');
      // Refetch the order so the status pill + any derived UI reflect the
      // new state (Booqable also fires order.reserved webhook → DB upsert).
      setLineRefetchTrigger((c) => c + 1);
      // Invalidate the Orders list cache so returning to /orders shows the
      // new status (and any newly-created orders) without a manual refresh.
      // The 5-min staleTime would otherwise keep the list frozen on the
      // pre-mutation snapshot.
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.concept });
      // Reserving holds stock, so the window-aware availability in the product
      // catalog just changed. Invalidate it so the product-picker dropdown
      // (useProductCatalog, keyed under inventory) refetches instead of showing
      // the pre-reserve count from its 5-min staleTime cache.
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Reserve failed';
      console.error('[reserve] error:', err);
      dispatchToast(
        <Toast>
          <ToastTitle>Could not reserve order</ToastTitle>
          <ToastBody>{msg}</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    } finally {
      setIsReserving(false);
    }
  }, [orderId, isNewOrder, isReserving, queryClient, dispatchToast]);

  // Manually send the booking-confirmation email. Auto-send on order.reserved is
  // OFF by default (only fires when RESERVE_CONFIRMATION_AUTO is set), so the
  // customer is never auto-emailed on reserve without a deliberate opt-in — staff
  // push it from here. The backend dedups (already-sent ⇒ no-op) so a stray
  // re-click can't double-email.
  const handleSendConfirmation = useCallback(async () => {
    if (!orderId || isNewOrder || isSendingConfirmation) return;
    setIsSendingConfirmation(true);
    try {
      const res = await apiFetch(
        `/booqable/orders/${encodeURIComponent(orderId)}/send-confirmation`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = body?.error || `Send failed (HTTP ${res.status})`;
        dispatchToast(
          <Toast>
            <ToastTitle>Could not send confirmation</ToastTitle>
            <ToastBody>{msg}</ToastBody>
          </Toast>,
          { intent: 'error' },
        );
        return;
      }
      if (body?.sent) {
        dispatchToast(
          <Toast>
            <ToastTitle>Confirmation email sent</ToastTitle>
            {body?.email ? <ToastBody>Sent to {body.email}</ToastBody> : null}
          </Toast>,
          { intent: 'success' },
        );
      } else {
        // Benign no-op (already sent, or no customer email on file) — explain
        // rather than imply a failure.
        const reasonMsg = body?.reason === 'already_sent'
          ? 'A confirmation was already sent for this order.'
          : body?.reason === 'no_email'
            ? 'No customer email on file for this order.'
            : 'No email was sent.';
        dispatchToast(
          <Toast>
            <ToastTitle>Confirmation not sent</ToastTitle>
            <ToastBody>{reasonMsg}</ToastBody>
          </Toast>,
          { intent: 'info' },
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Send failed';
      dispatchToast(
        <Toast>
          <ToastTitle>Could not send confirmation</ToastTitle>
          <ToastBody>{msg}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    } finally {
      setIsSendingConfirmation(false);
    }
  }, [orderId, isNewOrder, isSendingConfirmation, dispatchToast]);

  // Cancel the order (side exit, valid only from draft/reserved). Invoked
  // from the ⋯ overflow menu via a confirmation dialog. Optimistically flips
  // the pill to 'canceled'; the refetch + orders-list invalidation reconcile.
  const performCancel = useCallback(async () => {
    if (!orderId || isNewOrder || isTransitioning) return;
    setIsTransitioning(true);
    try {
      const res = await apiFetch(`/booqable/orders/${encodeURIComponent(orderId)}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        dispatchToast(
          <Toast>
            <ToastTitle>Could not cancel order</ToastTitle>
            <ToastBody>{body?.error || `Cancel failed (HTTP ${res.status})`}</ToastBody>
          </Toast>,
          { intent: 'error' },
        );
        return;
      }
      setOrderStatus('canceled');
      setLineRefetchTrigger((c) => c + 1);
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.concept });
      // Canceling releases held stock back into availability — refetch catalog.
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Cancel failed';
      dispatchToast(
        <Toast>
          <ToastTitle>Could not cancel order</ToastTitle>
          <ToastBody>{msg}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    } finally {
      setIsTransitioning(false);
    }
  }, [orderId, isNewOrder, isTransitioning, queryClient, dispatchToast]);

  // Revert an order back to an earlier status: archived → stopped (un-archive),
  // stopped → started/reserved/draft, started → reserved/draft, or reserved →
  // draft (un-reserve). Booqable
  // steps back via OrderStatusTransitions with revert, jumping straight to the
  // target in one call. `target` comes from the chosen Revert menu item,
  // confirmed via dialog.
  const performRevert = useCallback(async (target: 'draft' | 'reserved' | 'started' | 'stopped') => {
    if (!orderId || isNewOrder || isReverting) return;
    setIsReverting(true);
    try {
      const res = await apiFetch(`/booqable/orders/${encodeURIComponent(orderId)}/revert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: target }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        dispatchToast(
          <Toast>
            <ToastTitle>Could not revert order</ToastTitle>
            <ToastBody>{body?.error || `Revert failed (HTTP ${res.status})`}</ToastBody>
          </Toast>,
          { intent: 'error' },
        );
        return;
      }
      setOrderStatus(target);
      setLineRefetchTrigger((c) => c + 1);
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.concept });
      // Reverting un-reserves / un-starts stock, changing availability — refetch catalog.
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Revert failed';
      dispatchToast(
        <Toast>
          <ToastTitle>Could not revert order</ToastTitle>
          <ToastBody>{msg}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    } finally {
      setIsReverting(false);
    }
  }, [orderId, isNewOrder, isReverting, queryClient, dispatchToast]);

  // Inline price override — staff edits the Unit Price cell on a line and
  // commits a new value. Optimistically updates the row, then PUTs to the
  // backend which forwards to Booqable.set_line_price. Refetch reconciles.
  // Used by Tier D / unresolved-tier / any manual-quote scenario.
  const handleUpdateLinePrice = useCallback(async (lineId: string, priceInCents: number) => {
    if (!orderId || isNewOrder) return;
    // Hold the override so the post-PUT reconcile can't clobber it with a stale
    // (eventually-consistent) echo of the old price. Cleared on the reconcile
    // whose echo matches this value — see pendingLinePriceRef.
    pendingLinePriceRef.current.set(lineId, priceInCents);
    setOrderLines((prev) =>
      prev.map((l) =>
        l.id === lineId
          ? {
              ...l,
              priceInCents,
              totalInCents: priceInCents * (l.quantity || 1),
            }
          : l
      )
    );
    // Recompute the summary from the live lines until the refetch reconciles.
    setOptimisticTotals(true);
    try {
      const res = await apiFetch(
        `/booqable/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(lineId)}/price`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ price_each_in_cents: priceInCents }),
        }
      );
      if (!res.ok) {
        console.error('[update-line-price] failed:', res.status, await res.text());
      }
    } catch (err) {
      console.error('[update-line-price] error:', err);
    } finally {
      // payment-status pill is reseeded by the refetch (see derivePaymentStatus
      // seed in fetchOrderData) — no separate ?fields[orders]=… fetch needed.
      setLineRefetchTrigger((c) => c + 1);
      // A manual price edit changes the line total and therefore the payment
      // Due in the sidebar, which reads from the ['order-documents'] query
      // (not part of the order refetch) — refresh it so the sidebar tracks the
      // new total immediately instead of waiting on a manual page refresh.
      refreshOrderInvoices();
    }
  }, [orderId, isNewOrder, refreshOrderInvoices]);

  const handleUpdateLineExtraInfo = useCallback(async (lineId: string, extraInformation: string) => {
    if (!orderId || isNewOrder) return;
    // Optimistic: reflect the note immediately; refetch reconciles.
    setOrderLines((prev) =>
      prev.map((l) => (l.id === lineId ? { ...l, extraInformation } : l))
    );
    try {
      const res = await apiFetch(`/booqable/lines/${encodeURIComponent(lineId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ extra_information: extraInformation }),
      });
      if (!res.ok) {
        console.error('[update-line-extra-info] failed:', res.status, await res.text());
      }
    } catch (err) {
      console.error('[update-line-extra-info] error:', err);
    } finally {
      setLineRefetchTrigger((c) => c + 1);
      // Keep the sidebar in lockstep with the line list on any line change.
      refreshOrderInvoices();
    }
  }, [orderId, isNewOrder, refreshOrderInvoices]);

  const handleRemoveLine = useCallback(async (lineId: string) => {
    // Optimistic remove — refetch on completion reconciles totals. Mark this
    // line ID as deleted so the refetch effect filters it out even if
    // Booqable's eventually-consistent included[] still echoes it back.
    // Capture wasLastLine inside the functional setter so we don't need
    // orderLines in the deps array (its declaration sits below this callback).
    let wasLastLine = false;
    deletedLineIdsRef.current.add(lineId);
    setOrderLines((prev) => {
      wasLastLine = prev.length <= 1;
      return prev.filter((l) => l.id !== lineId);
    });
    try {
      const res = await apiFetch(`/booqable/lines/${encodeURIComponent(lineId)}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        console.error('[remove-line] failed:', res.status, await res.text());
      }
    } catch (err) {
      console.error('[remove-line] error:', err);
    } finally {
      setLineRefetchTrigger((c) => c + 1);
      // Removing a line frees its stock back into the window — refresh the
      // catalog so the picker's availability count climbs back immediately.
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      // Backend archives any pro forma when the last line is removed; refetch
      // the sidebar so it reflects either the still-present pro forma or its
      // disappearance.
      if (orderId && !isNewOrder) {
        // payment-status is reseeded by the refetch above (see derivePaymentStatus
        // seed in fetchOrderData), so it no longer needs its own GET here.
        // Documents is a separate list (not in the order include), so invalidate it.
        queryClient.invalidateQueries({ queryKey: ['order-documents', orderId] });
        // Booqable's archive propagation is eventually consistent — the
        // immediate refetch right above sometimes still sees the just-archived
        // pro forma. Schedule a second invalidate ~1.5s later as a safety
        // net for the last-line case (the only time we expect an archive).
        if (wasLastLine) {
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ['order-documents', orderId] });
          }, 1500);
        }
      }
    }
  }, [orderId, isNewOrder, queryClient]);

  const [productSearch, setProductSearch] = useState('');
  const [addFieldDialogOpen, setAddFieldDialogOpen] = useState(false);
  const [customFieldLabel, setCustomFieldLabel] = useState('');
  const [customFieldDataType, setCustomFieldDataType] = useState('Single line text');
  const [customFieldValue, setCustomFieldValue] = useState('');
  const [showOnQuotes, setShowOnQuotes] = useState(false);
  const [showOnContracts, setShowOnContracts] = useState(false);
  const [showOnInvoices, setShowOnInvoices] = useState(false);
  const [showOnPackingSlip, setShowOnPackingSlip] = useState(false);
  const [billingAddressDialogOpen, setBillingAddressDialogOpen] = useState(false);
  const [savedAddressOption, setSavedAddressOption] = useState('new');
  const [billingCountry, setBillingCountry] = useState('United States');
  const [billingAddressLine1, setBillingAddressLine1] = useState('');
  const [billingAddressLine2, setBillingAddressLine2] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingRegion, setBillingRegion] = useState('');
  const [billingPostalCode, setBillingPostalCode] = useState('');
  const [savedBillingAddress, setSavedBillingAddress] = useState<string | null>(null);
  // Order-level PO number — TWM-side field (Booqable doesn't store it). Sent
  // to Auth.Net's order.purchaseOrderNumber on card charge for govt customers.
  const [orderPoNumber, setOrderPoNumber] = useState('');
  const [orderPaymentTerms, setOrderPaymentTerms] = useState(DEFAULT_PAYMENT_TERMS);
  // Real-life project name (orders.project_name) — TWM-local free-text label
  // that renders on the invoice. Not the multi-phase project.
  const [orderProjectName, setOrderProjectName] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  // Monthly-rental flag (orders.is_monthly) — TWM-local. When on, the billing
  // section shows a month picker instead of delivery/pick-up dates and the
  // rental window spans the chosen calendar month. Display/period only.
  const [isMonthly, setIsMonthly] = useState(false);
  // Manual PR services-withholding amount override (orders.pr_withholding_amount_cents),
  // in whole cents. null = no override → the holdback is computed from the rate.
  // A value (incl. 0) overrides the rate outright — the escape hatch for a one-off
  // correction (e.g. deducting the 10% a prior invoice missed). TWM-local; persisted
  // via PUT /orders/:id and applied to the invoice PDF + pay link.
  const [withholdingAmountCents, setWithholdingAmountCents] = useState<number | null>(null);
  // Project-phase context. Non-null only when this order is a child of a
  // multi-phase project — then its location is owned by the project (rendered
  // read-only) and its dates are part of a contiguous swap chain edited via the
  // local phaseSchedule + the batch save (no per-field round-trip).
  const [orderProject, setOrderProject] = useState<OrderProjectCtx | null>(null);
  const [phaseSchedule, setPhaseSchedule] = useState<EditablePhase[]>([]);
  // Last-persisted schedule, for Discard to restore to.
  const phaseScheduleBaselineRef = useRef<EditablePhase[]>([]);
  const [phaseScheduleDirty, setPhaseScheduleDirty] = useState(false);
  // Persisted across refresh + shared with the ProjectDetails schedule editor.
  const [phaseShiftLater, setPhaseShiftLater] = usePhaseCascadePref();
  const [phaseSaving, setPhaseSaving] = useState(false);
  const [phaseSaveError, setPhaseSaveError] = useState<string | null>(null);
  // TESTING affordance: when on, a phase order's project-owned fields (location,
  // dates, PO) unlock for direct editing on this page instead of being managed
  // by the parent project. Resets on reload (intentionally not persisted).
  const [allowProjectOverride, setAllowProjectOverride] = useState(false);
  const [customerFields, setCustomerFields] = useState<CustomerField[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  // Progressive render: the page chrome (header, customer, dates, location)
  // paints from the fast local mirror as soon as `loadingOrder` clears, while
  // `loadingLines` gates ONLY the products/totals section until the heavy
  // Booqable include resolves. Seeded true for a promoted order's full load.
  const [loadingLines, setLoadingLines] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [orderStatus, setOrderStatus] = useState<string | null>(null);
  // Booqable id of the loaded order. Null while the order is a Supabase-only
  // local draft (the /orders/new flow defers Booqable creation). `isPromoted`
  // gates every Booqable-touching feature (transitions, billing, invoices,
  // line POSTs) — autosave/dates/location/tier-probe work on the local row
  // regardless. Set by the order-load effect and by promoteOrder().
  const [orderBooqableId, setOrderBooqableId] = useState<string | null>(null);
  const isPromoted = !!orderBooqableId;
  // Guards single promotion (reset on failure so a later trigger retries).
  const promotionStartedRef = useRef(false);
  // Order-level payment_status lives in its own TanStack query so the
  // OrderRightSidebar can invalidate it after recording a manual payment
  // and the header pill refreshes without a full page reload. The main
  // order fetch below intentionally doesn't touch this — keeping it
  // separate avoids tangling the rest of the order load logic.
  const paymentStatusQuery = useQuery<{ payment_status: string | null }>({
    queryKey: ['order-payment-status', orderId],
    queryFn: async () => {
      const res = await apiFetchWithRetry(
        `/booqable/orders/${encodeURIComponent(orderId!)}?fields[orders]=payment_status,grand_total_with_tax_in_cents,grand_total_in_cents,amount_paid_in_cents`,
      );
      if (!res.ok) throw new Error(`payment-status fetch failed: ${res.status}`);
      const data = await res.json();
      const attrs = data?.data?.attributes || {};
      return { payment_status: derivePaymentStatus(attrs) };
    },
    // Hits the Booqable proxy — only valid once the order is promoted.
    enabled: !!orderId && isPromoted,
    staleTime: 30 * 1000,
  });
  const paymentStatus = paymentStatusQuery.data?.payment_status ?? null;

  // Prohibited-items acknowledgment gate. Admin-created orders must have the
  // terms acknowledged on the customer's behalf before they can be reserved;
  // the backend enforces the same at /reserve (app.py — code
  // 'prohibited_items_unacknowledged'), and this disables the Reserve button
  // proactively instead of failing the click. Website orders are gated at
  // checkout; sync orders aren't admin-source, so neither is blocked here.
  // Reads the shared local-row cache (queryKeys.orders.localRow) — the same
  // row the load effect and the right sidebar fetch — so the prohibited-items
  // gate doesn't issue its own GET /orders/:id. `source` and
  // `prohibited_items_acknowledged` are fields on the full row.
  const placementQuery = useQuery<{
    data?: { source?: string | null; prohibited_items_acknowledged?: boolean | null };
  }>({
    queryKey: queryKeys.orders.localRow(orderId!),
    queryFn: () => fetchOrderLocalRow(orderId!),
    enabled: !isNewOrder && !!orderId,
    staleTime: 30 * 1000,
  });
  const missingProhibitedAck =
    placementQuery.data?.data?.source === 'admin' &&
    placementQuery.data?.data?.prohibited_items_acknowledged !== true;
  // True once money has changed hands. Booqable's finalized invoice is an
  // immutable legal record (see app.py archive-invoice guard) and TWM prices
  // lines with explicit overrides, so moving the rental period on a paid order
  // does NOT re-bill — extensions must be charged via an Extra Day line. We use
  // this to gate the date pickers behind a confirmation dialog.
  const isInvoicePaid =
    !!paymentStatus &&
    ['partially_paid', 'payment_paid', 'overpaid'].includes(paymentStatus);
  // Holds a pending rental-period edit on a paid order until the user confirms
  // it in the dialog. Null when no edit is awaiting confirmation.
  const [pendingPeriodChange, setPendingPeriodChange] = useState<
    { field: 'pickup' | 'return'; value: string } | null
  >(null);
  const [recalcBanner, setRecalcBanner] = useState<{ reason: 'location' | 'period' | 'tax' } | null>(null);
  const [orderSubtotalCents, setOrderSubtotalCents] = useState<number>(0);
  const [orderTaxCents, setOrderTaxCents] = useState<number>(0);
  // Human-readable tax breakdown for the summary's Tax line, e.g. "IVU 11.5%".
  // Built from Booqable's tax_values sideload (one entry per applied rate).
  const [orderTaxLabel, setOrderTaxLabel] = useState<string | null>(null);
  const [orderTotalCents, setOrderTotalCents] = useState<number>(0);
  // Optimistic order totals. A line price/qty edit updates the affected row
  // instantly, but Booqable's authoritative order Subtotal/Tax/Total only land
  // on the eventually-consistent refetch — which doesn't fire until the slow
  // set_line_price/PATCH round trip resolves (the multi-second "totals lag").
  // While an edit is in flight we recompute the summary from the live line
  // rows using the last authoritative blended tax rate; authoritative values
  // resume the moment the reconcile lands and clears this flag.
  const [optimisticTotals, setOptimisticTotals] = useState(false);
  const effectiveTaxRateRef = useRef(0);
  const { displaySubtotalCents, displayTaxCents, displayTotalCents } = useMemo(() => {
    if (!optimisticTotals) {
      return {
        displaySubtotalCents: orderSubtotalCents,
        displayTaxCents: orderTaxCents,
        displayTotalCents: orderTotalCents,
      };
    }
    // totalInCents is unit price × qty across this codebase (rental cycle is
    // already baked into priceInCents), so summing priceInCents × quantity
    // reproduces Booqable's pre-tax price_in_cents (all lines, incl. fees).
    const subtotal = orderLines.reduce(
      (sum, l) => sum + (l.priceInCents || 0) * (l.quantity || 1),
      0,
    );
    const tax = Math.round(subtotal * effectiveTaxRateRef.current);
    return {
      displaySubtotalCents: subtotal,
      displayTaxCents: tax,
      displayTotalCents: subtotal + tax,
    };
  }, [optimisticTotals, orderLines, orderSubtotalCents, orderTaxCents, orderTotalCents]);

  // Catalog (groups + variations + per-item stock + per-window availability)
  // shared with the product picker. Both consumers pass the same ISO date
  // strings so the React Query cache key matches and only one network call
  // happens per (window, stale-window) — see ProductsSection for where these
  // same strings are forwarded to the picker.
  const fromDateIso = pickupDate ? new Date(pickupDate + 'T00:00:00').toISOString() : undefined;
  const tillDateIso = returnDate ? new Date(returnDate + 'T23:59:59').toISOString() : undefined;
  const { stockMap, productGroups } = useProductCatalog({ fromDate: fromDateIso, tillDate: tillDateIso });
  const [emailHistoryOpen, setEmailHistoryOpen] = useState(false);
  const [emailHistoryEntries, setEmailHistoryEntries] = useState<EmailHistoryEntry[]>([]);

  // Window-available count per item id (#2). Populated only when a rental
  // window is set — the catalog's per-variation `available` is the count
  // bookable for THIS order's window. Used to cap the qty SpinButton and to
  // flag a shortage add. Empty when no window (then stockCount is the cap).
  const availableMap = React.useMemo(() => {
    const map: Record<string, number> = {};
    for (const g of productGroups || []) {
      for (const v of g.variations || []) {
        if (v.available !== undefined) map[v.id] = v.available as number;
      }
    }
    return map;
  }, [productGroups]);

  // Dismissable white/orange shortage banner shown after a warn-and-allow add of a
  // line with no units available for the window (#2). Cleared on navigation to
  // another order so it never leaks across orders.
  const [shortageNotice, setShortageNotice] = useState<{ message: string } | null>(null);
  useEffect(() => {
    setShortageNotice(null);
  }, [orderId]);

  // Location-related state
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);
  const coordinateSaveTimeout = useRef<NodeJS.Timeout | null>(null);
  // Marker for "initial load complete" — used by the autosave effect so it
  // doesn't fire on hydration. Stays `undefined` until the order's customer
  // (or absence thereof) is resolved on first fetch.
  const initialCustomerId = useRef<string | null | undefined>(undefined);
  // Booqable address ID for this order's billing address, populated on order
  // load when one exists. The billing modal's Save button PATCHes this row
  // when set, POSTs a new one when null.
  const billingAddressIdRef = useRef<string | null>(null);
  // Mirrors `savedBillingAddress` so the dev-poll closure can drift-detect
  // without including the state in its deps (which would tear down and
  // rebuild the interval on every change).
  const savedBillingValueRef = useRef<string | null>(null);
  const [savingBillingAddress, setSavingBillingAddress] = useState(false);
  const [billingSaveError, setBillingSaveError] = useState<string | null>(null);
  // Tax follows order.tax_region (derived from the customer). We snapshot the
  // region the order was last taxed under (orderTaxRegionRef, set on load /
  // after recalc) and the currently-selected customer's region
  // (customerTaxRegionRef) so a customer swap into a different tax profile can
  // prompt a price recalculation.
  const orderTaxRegionRef = useRef<string | null>(null);
  const customerTaxRegionRef = useRef<string | null>(null);

  // Customer's main_address property — surfaced as the "Saved address"
  // option in the picker. When the order's billing_address_property_id
  // points here, mode is "customer's address"; otherwise "new"/edit.
  const [customerMainAddress, setCustomerMainAddress] = useState<{
    id: string;
    display: string;
    fields: {
      address1: string;
      address2: string;
      city: string;
      region: string;
      zipcode: string;
      country: string;
    };
  } | null>(null);

  // 'customer' = reference customer's main_address; 'new' = inline create/update.
  const [billingMode, setBillingMode] = useState<'customer' | 'new'>('new');

  // Snapshot of the autosave-tracked fields as of the last successful load
  // or save. The watcher compares current values to this; if everything
  // matches, no save is scheduled — which prevents a spurious save firing
  // on initial hydration.
  //
  // Billing address fields are intentionally NOT tracked here: the billing
  // dialog has its own Save button and persistence path.
  type AutoSaveSnapshot = {
    customerId: string | null;
    pickupDate: string;
    returnDate: string;
    poNumber: string;
    paymentTerms: string;
    projectName: string;
    isMonthly: boolean;
  };
  const autosaveSnapshotRef = useRef<AutoSaveSnapshot | null>(null);
  const [currentLocationAddress, setCurrentLocationAddress] = useState('');
  const [currentLocationName, setCurrentLocationName] = useState('');
  // Saved customer locations come from the new customer_locations table via
  // /customers/:id/locations. Kept in the legacy {label, lat, lng} shape so
  // the order form's location picker doesn't need to change.
  const customerLocationsQuery = useQuery({
    queryKey: queryKeys.customers.locations(selectedCustomer?.id || ''),
    queryFn: () => fetchCustomerLocations(selectedCustomer!.id),
    enabled: !!selectedCustomer?.id,
  });
  // PR services-withholding: the selected customer's default rate drives the
  // holdback shown in the order summary. selectedCustomer is a lightweight
  // {id,name,email} shape (no rate), so fetch the full customer for its
  // prWithholdingRate. null = not a withholding agent → no row.
  const customerWithholdingQuery = useQuery({
    queryKey: queryKeys.customers.detail(selectedCustomer?.id || ''),
    queryFn: () => fetchCustomerById(selectedCustomer!.id),
    enabled: !!selectedCustomer?.id,
    staleTime: 5 * 60 * 1000,
  });
  // PR services-withholding for the order summary — same half-up arithmetic as
  // the invoice PDF. A manual per-order amount override (withholdingAmountCents)
  // wins outright and carries no rate label; otherwise the holdback is the
  // customer default rate on the pre-tax subtotal. The invoice PDF/preview remain
  // authoritative. 0 → no row. (Declared after the query above — it reads
  // customerWithholdingQuery — and after displaySubtotalCents.)
  const { amountCents: orderWithholdingCents, rateLabel: orderWithholdingLabel } =
    resolveWithholdingAmount(
      withholdingAmountCents,
      undefined,
      customerWithholdingQuery.data?.prWithholdingRate,
      displaySubtotalCents,
    );
  // The automatic (rate-based) holdback, ignoring any manual override — shown as
  // a reference in the adjustment dialog so staff can match or depart from it.
  const { amountCents: autoWithholdingCents, rateLabel: autoWithholdingLabel } =
    resolveWithholdingAmount(
      undefined,
      undefined,
      customerWithholdingQuery.data?.prWithholdingRate,
      displaySubtotalCents,
    );
  // Persist the manual withholding-amount override. cents = the flat holdback
  // (0 zeroes it out); null clears the override back to the rate computation.
  // TWM-local column, so this hits PUT /orders/:id directly (never Booqable) and
  // works for promoted and un-promoted orders alike. Optimistic: set state first
  // so the summary updates immediately, then persist.
  const handleSetWithholdingAmount = async (cents: number | null): Promise<void> => {
    if (!orderId) return;
    const prev = withholdingAmountCents;
    setWithholdingAmountCents(cents);
    try {
      const response = await apiFetch(`/orders/${orderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pr_withholding_amount_cents: cents }),
      });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`PUT /orders/${orderId} failed: HTTP ${response.status} ${text.slice(0, 200)}`);
      }
      // Refresh the local mirror cache so a re-open reflects the saved value.
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.localRow(orderId) });
    } catch (err) {
      // Roll back the optimistic state on failure so the UI doesn't lie.
      setWithholdingAmountCents(prev);
      throw err;
    }
  };
  const customerLocations = useMemo<{ label: string; lat: number; lng: number; address: string | null }[]>(
    () => (customerLocationsQuery.data || [])
      .filter((l: SavedCustomerLocation) => l.latitude != null && l.longitude != null)
      .map((l: SavedCustomerLocation) => ({
        label: l.name || '',
        lat: Number(l.latitude),
        lng: Number(l.longitude),
        address: l.address ?? null,
      })),
    [customerLocationsQuery.data],
  );
  const [selectedLocationIndex, setSelectedLocationIndex] = useState<number | null>(null);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [newLocationLabel, setNewLocationLabel] = useState('');
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; lat: number; lng: number } | null>(null);
  const [removeCustomerDialogOpen, setRemoveCustomerDialogOpen] = useState(false);
  // Create-customer drawer, opened in-place by the Add-customer icon in the
  // Customer section so staff can create + attach a customer without leaving
  // the order. Mirrors the OfficeDashboard quick-action wiring.
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [saveLocationDialogOpen, setSaveLocationDialogOpen] = useState(false);
  const [saveLocationLabel, setSaveLocationLabel] = useState('');

  // Restore the new-order form from localStorage (and from the
  // ?customer=<id> query param if the user landed here via "Add Order" on
  // a customer profile). Query-param is a one-shot prefill: read, then
  // strip it from the URL so a subsequent refresh doesn't clobber a
  // customer the user has since changed.
  //
  // Synchronous rehydration: the scratchpad caches customer name/email
  // alongside the id, so a refresh repaints the customer card immediately
  // — no /booqable/customers fetch in the critical path. Only fall back
  // to a network fetch when we have an id but no cached name (URL-prefill
  // first-load, or a legacy/partial scratch entry). Runs once per mount.
  useEffect(() => {
    if (!isNewOrder) {
      scratchRestoredRef.current = true;
      return;
    }
    let cancelled = false;
    const scratch = readOrderDraftScratch();
    const customerParam = searchParams.get('customer');

    if (scratch) {
      if (scratch.pickup) setPickupDate(scratch.pickup);
      if (scratch.return) setReturnDate(scratch.return);
      if (scratch.locationAddress) setCurrentLocationAddress(scratch.locationAddress);
      if (scratch.locationName) setCurrentLocationName(scratch.locationName);
      if (scratch.locationLat != null && scratch.locationLng != null) {
        setCurrentLocation({ lat: scratch.locationLat, lng: scratch.locationLng });
      }
      if (scratch.customerId && scratch.customerName) {
        setSelectedCustomer({
          id: scratch.customerId,
          name: scratch.customerName,
          email: scratch.customerEmail || '',
        });
      }
    }

    // Consume the query param so it doesn't keep applying on refresh.
    if (customerParam) {
      const next = new URLSearchParams(searchParams);
      next.delete('customer');
      setSearchParams(next, { replace: true });
    }

    // Network fallback: only when we have an id but no cached name
    // (URL-prefill first load, or partial scratch). Query-param always
    // wins over a stored customer — it's the most recent expressed intent.
    const needsFetch =
      customerParam || (scratch?.customerId && !scratch.customerName);
    const customerIdToFetch =
      customerParam || (scratch?.customerId && !scratch.customerName ? scratch.customerId : null);

    if (!needsFetch) {
      scratchRestoredRef.current = true;
      return () => { cancelled = true; };
    }

    (async () => {
      try {
        if (customerIdToFetch) {
          const response = await apiFetch(`/booqable/customers/${customerIdToFetch}`);
          if (response.ok) {
            const result = await response.json();
            const data = result?.data;
            if (data && !cancelled) {
              const attrs = data.attributes || {};
              setSelectedCustomer({
                id: data.id,
                name: attrs.name || '',
                email: attrs.email || '',
              });
            }
          }
        }
      } catch (err) {
        console.error('Failed to restore customer on /orders/new:', err);
      } finally {
        if (!cancelled) scratchRestoredRef.current = true;
      }
    })();

    return () => { cancelled = true; };
    // Run once on mount — restore must not re-fire on field changes or it
    // would clobber the user's edits with the stored scratchpad.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist new-order form fields to localStorage on every change so a
  // refresh / accidental nav doesn't lose work. Gated on restore having
  // completed so the first paint doesn't wipe the scratchpad with empty
  // defaults. No-op on existing orders.
  useEffect(() => {
    if (!isNewOrder) return;
    if (!scratchRestoredRef.current) return;
    writeOrderDraftScratch({
      customerId: selectedCustomer?.id ?? null,
      customerName: selectedCustomer?.name ?? null,
      customerEmail: selectedCustomer?.email ?? null,
      pickup: pickupDate,
      return: returnDate,
      locationAddress: currentLocationAddress,
      locationName: currentLocationName,
      locationLat: currentLocation?.lat ?? null,
      locationLng: currentLocation?.lng ?? null,
    });
  }, [
    isNewOrder,
    selectedCustomer?.id,
    selectedCustomer?.name,
    selectedCustomer?.email,
    pickupDate,
    returnDate,
    currentLocationAddress,
    currentLocationName,
    currentLocation?.lat,
    currentLocation?.lng,
  ]);

  // Set Mapbox access token
  mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_API_KEY || '';

  // If stockMap resolves after the order fetch finishes (or if a refetch
  // brings in lines with new item_ids), backfill stockCount on existing rows.
  useEffect(() => {
    if (Object.keys(stockMap).length === 0 && Object.keys(availableMap).length === 0) return;
    setOrderLines((prev) => {
      let changed = false;
      const next = prev.map((l) => {
        const total = l.itemId ? stockMap[l.itemId] : undefined;
        const avail = l.itemId ? availableMap[l.itemId] : undefined;
        const nextStock = total !== undefined ? total : l.stockCount;
        const nextAvail = avail !== undefined ? avail : l.availableCount;
        if (nextStock !== l.stockCount || nextAvail !== l.availableCount) {
          changed = true;
          return { ...l, stockCount: nextStock, availableCount: nextAvail };
        }
        return l;
      });
      return changed ? next : prev;
    });
  }, [stockMap, availableMap]);

  // Same backfill for product metadata: if the catalog resolves after the
  // order fetch, fill in trackable/productType so sales-item lines (bulldog
  // bag) correctly hide the stock-item picker and rental duration.
  useEffect(() => {
    if (!productGroups || productGroups.length === 0) return;
    const meta = buildProductMeta(productGroups);
    setOrderLines((prev) => {
      let changed = false;
      const next = prev.map((l) => {
        const m = l.itemId ? meta[l.itemId] : undefined;
        if (m && (m.trackable !== l.trackable || m.productType !== l.productType)) {
          changed = true;
          return { ...l, trackable: m.trackable, productType: m.productType };
        }
        return l;
      });
      return changed ? next : prev;
    });
  }, [productGroups]);

  // Tracks which order id has finished its first load. Used to suppress the
  // full-page "Loading order..." spinner on background refetches (e.g. after
  // adding a line item) — those should update the page in place, not blank
  // it. Reset implicitly when orderId changes since the ref no longer matches.
  const loadedOrderIdRef = useRef<string | null>(null);
  // Distinguishes a first/full load of an order (re-seed every form field) from
  // a post-edit reconcile fired by lineRefetchTrigger, where only the
  // lines/totals/status slice changed. Holds the orderId of the last completed
  // load; when it equals the current orderId this run is a reconcile, so we skip
  // re-applying customer/billing/dates/PO/project/location/autosave (none of
  // which a line edit touches) and fetch a lighter Booqable include. NOT
  // pre-armed by the draft-create navigation (unlike loadedOrderIdRef), so the
  // post-navigate load is always treated as a full load.
  const reconciledOrderIdRef = useRef<string | null>(null);

  // Fetch order data when editing existing order
  useEffect(() => {
    const fetchOrderData = async () => {
      if (isNewOrder || !orderId) {
        return;
      }

      try {
        // Pre-armed by the autosave handler when navigating from /orders/new
        // to /orders/:id — skip the spinner so the page doesn't flash, but
        // still let the fetch run so orderNumber/status populate the header.
        if (loadedOrderIdRef.current !== orderId) {
          setLoadingOrder(true);
        }

        // A reconcile is a re-run for the same already-loaded order (fired by
        // lineRefetchTrigger after a line/status mutation). Only the
        // lines/totals/status slice can have changed, so the form-state setters
        // below are skipped and a lighter Booqable include is fetched.
        const isReconcile = reconciledOrderIdRef.current === orderId;

        // First read the local mirror to learn whether this order has been
        // promoted to Booqable yet. Un-promoted local drafts (booqable_id NULL)
        // have no Booqable resource, so the /booqable fetch below would 404 —
        // populate from the local row and stop. The form (autosave, dates,
        // location, tier probe) works the same; the picker/transitions stay
        // gated on isPromoted until promotion mints the Booqable order.
        // Shared with the right-sidebar (waive-fee) and customer-notes
        // (placement/ack) gates via queryKeys.orders.localRow. fetchQuery
        // populates that one cache, so those sibling useQuery observers dedupe
        // onto this single request instead of each firing their own GET
        // /orders/:id. A reconcile forces a fresh read (staleTime 0) because
        // status/number may have just changed; a first/normal load tolerates a
        // 30s-fresh cache hit (instant repeat-open).
        const localEnvelope = await queryClient.fetchQuery({
          queryKey: queryKeys.orders.localRow(orderId),
          queryFn: () => fetchOrderLocalRow(orderId),
          staleTime: isReconcile ? 0 : 30 * 1000,
        });
        const localPreData = localEnvelope?.data || {};
        const booqableId = localPreData.booqable_id || null;
        setOrderBooqableId(booqableId);
        // Manual withholding-amount override (TWM-local mirror field; present on
        // both the promoted and un-promoted paths). null = no override. Not part
        // of the field-autosave — it has its own explicit save (handleSetWithholdingAmount).
        if (!isReconcile) {
          setWithholdingAmountCents(
            typeof localPreData.pr_withholding_amount_cents === 'number'
              ? localPreData.pr_withholding_amount_cents
              : null,
          );
        }

        // In-order phases (sections) + the line→section tag map. The products
        // table reads lines live from Booqable (keyed by Booqable line id), so
        // we join section membership via the mirror's booqable_line_id.
        setOrderSections(Array.isArray(localPreData.sections) ? localPreData.sections : []);
        const tagMap: Record<string, string | null> = {};
        for (const l of (localPreData.lines || [])) {
          if (l && l.booqable_line_id) tagMap[l.booqable_line_id] = l.section_id || null;
        }
        setLineSectionMap(tagMap);

        // The TWM order number is minted at RESERVE by a DB trigger (NULL until
        // then). Reserve fires a reconcile, and a reconcile skips the form-state
        // block below — so apply the number here, on every pass, from the local
        // mirror (the authoritative orders.number, already committed by the
        // reserve transaction by the time this fetch runs). This lets the header
        // badge gain its #number the instant a draft is reserved, no refresh.
        if (localPreData.number) {
          setOrderNumber(String(localPreData.number));
        }

        // Phase context (present on both the local-draft and promoted paths —
        // both read localPreData). When this order is a project child, its
        // location is project-owned (read-only) and its dates are part of the
        // contiguous swap chain.
        // Skipped on reconcile — a line edit never changes project/phase, and
        // re-seeding would clobber unsaved phase-schedule edits.
        if (!isReconcile) {
        if (localPreData.project_id && localPreData.project) {
          const proj = localPreData.project;
          orderProjectIdRef.current = String(proj.id);
          setOrderProject({
            id: String(proj.id),
            projectNumber: proj.project_number,
            poNumber: proj.po_number ?? null,
            deliveryAddress: proj.delivery_address ?? null,
            deliveryLocationName: proj.delivery_location_name ?? null,
            deliveryLatitude: proj.delivery_latitude ?? null,
            deliveryLongitude: proj.delivery_longitude ?? null,
            phaseNumber: localPreData.phase_number ?? null,
            phaseCount: (proj.phases || []).length,
          });
          const seeded = phaseScheduleFromOrderData(proj);
          setPhaseSchedule(seeded);
          phaseScheduleBaselineRef.current = seeded;
          setPhaseScheduleDirty(false);
        } else {
          orderProjectIdRef.current = null;
          setOrderProject(null);
          setPhaseSchedule([]);
          phaseScheduleBaselineRef.current = [];
        }
        }

        if (!booqableId) {
          // Local-only draft: populate from the mirror, no Booqable round-trip.
          const c = localPreData.customer;
          if (c) {
            // Use the Booqable customer id so customer-field enrichment and
            // autosave round-trips resolve correctly.
            const cid = c.booqable_id || c.id;
            setSelectedCustomer({ id: cid, name: c.name || '', email: c.email || '' });
            initialCustomerId.current = cid;
          } else {
            initialCustomerId.current = null;
          }
          const lp = localPreData.starts_at
            ? new Date(localPreData.starts_at).toISOString().split('T')[0] : '';
          const lr = localPreData.stops_at
            ? new Date(localPreData.stops_at).toISOString().split('T')[0] : '';
          if (lp) setPickupDate(lp);
          if (lr) setReturnDate(lr);
          if (localPreData.delivery_latitude != null && localPreData.delivery_longitude != null) {
            const lat = Number(localPreData.delivery_latitude);
            const lng = Number(localPreData.delivery_longitude);
            setCurrentLocation({ lat, lng });
            setCurrentLocationAddress(localPreData.delivery_address || `${lat}, ${lng}`);
            if (localPreData.delivery_location_name) {
              setCurrentLocationName(localPreData.delivery_location_name);
            }
          }
          setOrderStatus(localPreData.status || 'draft');
          setOrderNumber(localPreData.number ? String(localPreData.number) : null);
          setOrderPoNumber(String(localPreData.po_number ?? ''));
          setOrderPaymentTerms(String(localPreData.payment_terms || DEFAULT_PAYMENT_TERMS));
          setOrderProjectName(String(localPreData.project_name ?? ''));
          setIsMonthly(Boolean(localPreData.is_monthly));
          setOrderLines([]);
          setOrderSubtotalCents(0);
          setOrderTaxCents(0);
          setOrderTotalCents(0);
          autosaveSnapshotRef.current = {
            customerId: c?.booqable_id || c?.id || null,
            pickupDate: lp,
            returnDate: lr,
            poNumber: String(localPreData.po_number ?? ''),
            paymentTerms: String(localPreData.payment_terms || DEFAULT_PAYMENT_TERMS),
            projectName: String(localPreData.project_name ?? ''),
            isMonthly: Boolean(localPreData.is_monthly),
          };
          setLoadingOrder(false);
          loadedOrderIdRef.current = orderId;
          reconciledOrderIdRef.current = orderId;
          return;
        }

        // --- Progressive render (phase 1) -----------------------------------
        // A promoted order. Seed the chrome (customer, dates, location, PO,
        // status, number) from the local mirror we already fetched, arm the
        // autosave baseline from those SAME values so the watcher's first run
        // sees current === snapshot (no spurious save), and paint the page NOW.
        // The heavy Booqable include below fills in lines/totals/tax/billing
        // under `loadingLines` — it no longer blocks the whole page. Phase 2
        // re-applies the authoritative Booqable values (identical for a synced
        // order, so the snapshot stays consistent). Skipped on reconcile, where
        // the page is already rendered and only lines/totals changed.
        if (!isReconcile) {
          const c = localPreData.customer;
          const cid = c ? (c.booqable_id || c.id) : null;
          if (c) {
            setSelectedCustomer({ id: cid, name: c.name || '', email: c.email || '' });
            initialCustomerId.current = cid;
          }
          const lp = localPreData.starts_at
            ? new Date(localPreData.starts_at).toISOString().split('T')[0] : '';
          const lr = localPreData.stops_at
            ? new Date(localPreData.stops_at).toISOString().split('T')[0] : '';
          if (lp) setPickupDate(lp);
          if (lr) setReturnDate(lr);
          setOrderPoNumber(String(localPreData.po_number ?? ''));
          setOrderPaymentTerms(String(localPreData.payment_terms || DEFAULT_PAYMENT_TERMS));
          setOrderProjectName(String(localPreData.project_name ?? ''));
          setIsMonthly(Boolean(localPreData.is_monthly));
          if (localPreData.delivery_latitude != null && localPreData.delivery_longitude != null) {
            const lat = Number(localPreData.delivery_latitude);
            const lng = Number(localPreData.delivery_longitude);
            setCurrentLocation({ lat, lng });
            setCurrentLocationAddress(localPreData.delivery_address || `${lat}, ${lng}`);
            if (localPreData.delivery_location_name) {
              setCurrentLocationName(localPreData.delivery_location_name);
            }
          }
          setOrderStatus(localPreData.status || 'draft');
          setOrderNumber(localPreData.number ? String(localPreData.number) : null);
          autosaveSnapshotRef.current = {
            customerId: cid,
            pickupDate: lp,
            returnDate: lr,
            poNumber: String(localPreData.po_number ?? ''),
            paymentTerms: String(localPreData.payment_terms || DEFAULT_PAYMENT_TERMS),
            projectName: String(localPreData.project_name ?? ''),
            isMonthly: Boolean(localPreData.is_monthly),
          };
          setLoadingOrder(false);
          // Caching: if a recent Booqable snapshot for this order is still in the
          // query cache (gcTime ~10min), show its lines/totals immediately and let
          // the fetch below revalidate in the background — a repeat open is then
          // instant. Otherwise spinner the products section until the include lands.
          const cachedLines = queryClient.getQueryData<{
            lines: OrderLineRow[];
            subtotalCents: number;
            taxCents: number;
            totalCents: number;
            taxLabel: string | null;
          }>(['order-booqable-cache', orderId]);
          if (cachedLines) {
            setOrderLines(cachedLines.lines);
            setOrderSubtotalCents(cachedLines.subtotalCents);
            setOrderTaxCents(cachedLines.taxCents);
            setOrderTotalCents(cachedLines.totalCents);
            setOrderTaxLabel(cachedLines.taxLabel);
            setLoadingLines(false);
          } else {
            setLoadingLines(true);
          }
        }

        // On a reconcile only the lines/totals/tax changed, so skip the
        // customer + properties (billing) sideloads we don't re-apply below —
        // this keeps the per-edit Booqable round trip (and its include
        // expansion) small. A full load pulls everything to seed the form.
        const includeParam = isReconcile
          ? 'lines.planning.stock_item_plannings.stock_item,tax_values'
          : 'customer,lines.planning.stock_item_plannings.stock_item,properties,tax_values';
        // Retry transient Booqable throttles (429) instead of throwing straight
        // to the fatal "Error loading order" screen — this heavy include-fetch
        // fires after every status transition and is the first to get rate-limited.
        const response = await apiFetchWithRetry(`/booqable/orders/${orderId}?include=${includeParam}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch order: ${response.statusText}`);
        }

        const result = await response.json();
        const orderData = result.data;
        const attrs = orderData.attributes || {};
        const included = result.included || [];

        // Baseline tax region this order was last taxed under — compared
        // against the selected customer's region to prompt a recalc on a
        // customer swap into a different tax profile.
        if (!isReconcile) {
          orderTaxRegionRef.current =
            attrs.tax_region_id ||
            orderData.relationships?.tax_region?.data?.id ||
            null;
        }

        // Extract customer from included data. Skipped on reconcile — the
        // lighter include omits the customer sideload and a line edit never
        // changes the order's customer.
        const customerId = orderData.relationships?.customer?.data?.id;
        const customerData = included.find((item: any) => item.type === 'customers' && item.id === customerId);

        if (!isReconcile) {
        if (customerData) {
          const customerAttrs = customerData.attributes || {};
          setSelectedCustomer({
            id: customerData.id,
            name: customerAttrs.name || '',
            email: customerAttrs.email || '',
          });
          initialCustomerId.current = customerData.id;
        } else if (localPreData.customer) {
          // Booqable didn't surface the customer on this order's response, but
          // the Supabase mirror has it (the same source the Orders list reads).
          // Fall back to it so the customer field isn't blank. Use the Booqable
          // customer id so enrichment + autosave round-trips still resolve.
          const c = localPreData.customer;
          const cid = c.booqable_id || c.id;
          setSelectedCustomer({ id: cid, name: c.name || '', email: c.email || '' });
          initialCustomerId.current = cid;
        } else {
          initialCustomerId.current = null;
        }
        }

        // Status only. `attrs.number` here is BOOQABLE's order number (this
        // fetch is the /booqable passthrough); the customer-facing number is
        // TWM-owned (orders.number) and was already set from the local mirror
        // above. Re-applying attrs.number would clobber it with Booqable's,
        // which is what surfaced the wrong # in the page title. Status, by
        // contrast, is reconciled here on every transition.
        if (attrs.status) {
          setOrderStatus(attrs.status);
        }

        // Extract billing address from included properties. Booqable v4
        // stores addresses as Property rows (property_type='address') keyed
        // by `identifier`, NOT a standalone /addresses resource.
        //

        // Source of truth for "what address is this order billing against"
        // is attrs.billing_address_property_id. Two cases:
        //   - Points at an order-owned billing_address property included in
        //     the response (we populate the form fields from it).
        //   - Points at the customer's main_address property (handled by
        //     the customerMainAddress effect — fields are populated when
        //     the dialog opens or when the user toggles the picker).
        // Billing address — skipped on reconcile (the lighter include omits the
        // properties sideload, and a line edit never changes billing).
        if (!isReconcile) {
        billingAddressIdRef.current = attrs.billing_address_property_id || null;
        const orderOwnedBilling = included.find(
          (item: any) =>
            item.type === 'properties' &&
            (
              // Prefer matching by the FK so we always pick up the right
              // row regardless of Booqable's owner_type casing/plural.
              (attrs.billing_address_property_id && item.id === attrs.billing_address_property_id) ||
              item.attributes?.identifier === 'billing_address'
            ),
        );
        if (orderOwnedBilling) {
          const a = orderOwnedBilling.attributes || {};
          if (a.address1) setBillingAddressLine1(a.address1);
          if (a.address2) setBillingAddressLine2(a.address2);
          if (a.city) setBillingCity(a.city);
          if (a.region) setBillingRegion(a.region);
          if (a.zipcode) setBillingPostalCode(a.zipcode);
          if (a.country) setBillingCountry(a.country);
          // Booqable composes a multi-line `value` string for display
          // (e.g. "123 Main St\nCity Region 00000\nCountry"). Prefer that
          // when present; fall back to a comma-joined version of the
          // structured fields. Drives the "saved" pill on the order page
          // so the address shows up immediately on load.
          const loadedDisplay =
            a.value ||
            [a.address1, a.address2, a.city, a.zipcode, a.region, a.country]
              .filter(Boolean)
              .join(', ');
          if (loadedDisplay) {
            setSavedBillingAddress(loadedDisplay);
            savedBillingValueRef.current = loadedDisplay;
          }
        } else {
          savedBillingValueRef.current = null;
        }
        }

        // Extract order dates. loadedPickup/loadedReturn are also read by the
        // autosave snapshot below, so compute them always; only the field
        // setters are skipped on reconcile (a line edit never changes dates,
        // and re-setting them would clobber an in-flight date edit).
        const loadedPickup = attrs.starts_at
          ? new Date(attrs.starts_at).toISOString().split('T')[0]
          : '';
        const loadedReturn = attrs.stops_at
          ? new Date(attrs.stops_at).toISOString().split('T')[0]
          : '';
        if (!isReconcile) {
          if (loadedPickup) setPickupDate(loadedPickup);
          if (loadedReturn) setReturnDate(loadedReturn);
        }

        // Extract line items from the included array. Booqable v4 line
        // attributes: `price_each_in_cents` is the unit price and
        // `price_in_cents` is the line total (qty × unit, pre-tax). There's
        // no per-line "total with tax" — tax is summarized on the order.
        // Build lookups across the included graph: planning_id → list of
        // SIPs, stock_item_id → identifier. Used below to surface the
        // specific stock units assigned to each line for cross-referencing
        // with Booqable's UI.
        const sipsByPlanning: Record<string, any[]> = {};
        const stockItemIdentifierById: Record<string, string> = {};
        // planning_id → planning.status. A planning goes 'started' (out with
        // the customer) then 'stopped' (returned) — independently of the order
        // status — and in either state Booqable 422s on removing the line or
        // its stock items.
        const planningStatusById: Record<string, string> = {};
        for (const it of included as any[]) {
          if (it.type === 'stock_item_plannings') {
            const pid = (it.attributes || {}).planning_id;
            if (pid) (sipsByPlanning[pid] = sipsByPlanning[pid] || []).push(it);
          } else if (it.type === 'stock_items') {
            const ident = (it.attributes || {}).identifier;
            if (ident) stockItemIdentifierById[it.id] = ident;
          } else if (it.type === 'plannings') {
            const st = (it.attributes || {}).status;
            if (st) planningStatusById[it.id] = st;
          }
        }

        const productMeta = buildProductMeta(productGroups);
        const lines: OrderLineRow[] = (included as any[])
          .filter((it) => it.type === 'lines')
          // Suppress lines the user just deleted — Booqable's get-order is
          // eventually consistent and can echo a just-DELETEd line for ~1s.
          .filter((it) => !deletedLineIdsRef.current.has(it.id))
          .map((it) => {
            const a = it.attributes || {};
            const itemId = a.item_id || undefined;
            const meta = itemId ? productMeta[itemId] : undefined;
            // line → planning is a hasone relationship on the line.
            const planningId = it.relationships?.planning?.data?.id;
            const sips = planningId ? sipsByPlanning[planningId] || [] : [];
            const stockUnits = sips
              .filter((s) => !(s.attributes || {}).archived)
              .map((s) => {
                const sid = s.relationships?.stock_item?.data?.id || '';
                const ident = stockItemIdentifierById[sid];
                return sid && ident ? { id: sid, identifier: ident } : null;
              })
              .filter((x): x is { id: string; identifier: string } => x !== null);
            const title = a.title || a.name || a.item_name || 'Unnamed';
            const kind: 'product' | 'custom' | 'convenience_fee' =
              itemId ? 'product' :
              (title || '').trim() === 'Convenience fee' ? 'convenience_fee' :
              'custom';
            const quantity = Number(a.quantity ?? 1);
            let priceInCents = Number(a.price_each_in_cents ?? 0);
            let totalInCents = Number(a.price_in_cents ?? 0);
            // Re-apply a just-committed price override until Booqable's echo
            // catches up to it; otherwise an eventually-consistent stale price
            // would clobber the override and snap the cell back to the default.
            const pendingPrice = pendingLinePriceRef.current.get(it.id);
            if (pendingPrice !== undefined) {
              if (priceInCents === pendingPrice) {
                pendingLinePriceRef.current.delete(it.id);
              } else {
                priceInCents = pendingPrice;
                totalInCents = pendingPrice * quantity;
              }
            }
            return {
              id: it.id,
              itemId,
              name: title,
              quantity,
              priceInCents,
              totalInCents,
              // Use the eagerly-loaded global stock map so SpinButton caps on
              // first render. If stockMap hasn't resolved yet, the merge effect
              // below fills it in once it does.
              stockCount: itemId ? stockMap[itemId] : undefined,
              // Window-available cap for the qty SpinButton (#2). Falls back to
              // stockCount inside LineQty when undefined (no window set).
              availableCount: itemId ? availableMap[itemId] : undefined,
              stockUnits,
              planningId,
              // Drives hiding the line/stock-pill remove affordances + the
              // green "Started"/"Stopped" label in ProductsSection.
              started: planningId ? planningStatusById[planningId] === 'started' : false,
              stopped: planningId ? planningStatusById[planningId] === 'stopped' : false,
              kind,
              // Trackable rentals get the stock-item picker + rental duration;
              // non-trackable sales items (e.g. bulldog bag) hide both. If the
              // catalog hasn't resolved yet, the merge effect below backfills.
              trackable: meta?.trackable,
              productType: meta?.productType,
              extraInformation: a.extra_information || '',
              // The auto-created tonnage overage line carries its $ in the price
              // at qty 1 (Booqable can't hold a fractional qty), so hide the
              // meaningless "1" — the tons live in the line note instead. Tonnage
              // is per weighed line now (several overage lines possible), so we
              // tag by the fixed overage title rather than a single order-level
              // id; the legacy id check stays for orders booked under the old
              // single-bin model.
              displayOverride:
                it.id === localPreData.tonnage_overage_line_id ||
                (kind === 'custom' && (title || '').trim() === 'Heavy material overage')
                  ? { showQty: false }
                  : undefined,
            };
          });
        setOrderLines(lines);
        // Seed the payment-status query straight from this response — the heavy
        // include already carries payment_status / grand_total* / amount_paid,
        // so the pill no longer needs its own ?fields[orders]=… GET after every
        // line add/remove (those handlers used to invalidate it, forcing a
        // redundant round-trip). Manual-payment flows still invalidate it to
        // force a true refetch; this just keeps it fresh on order refetches.
        if (orderId) {
          queryClient.setQueryData(
            ['order-payment-status', orderId],
            { payment_status: derivePaymentStatus(attrs) },
          );
        }
        // Order subtotal = sum of line totals before tax. Booqable exposes
        // this as `price_in_cents` on the order (and `grand_total_in_cents`
        // matches when there are no order-level discounts).
        const authSubtotalCents = Number(attrs.price_in_cents ?? 0);
        const authTaxCents = Number(attrs.tax_in_cents ?? 0);
        setOrderSubtotalCents(authSubtotalCents);
        setOrderTaxCents(authTaxCents);
        // Capture the blended effective tax rate (tax ÷ pre-tax subtotal) so the
        // optimistic-totals estimate can scale tax with a line edit, and clear
        // the optimistic flag now that authoritative numbers have landed.
        effectiveTaxRateRef.current =
          authSubtotalCents > 0 ? authTaxCents / authSubtotalCents : 0;
        setOptimisticTotals(false);
        // Build the tax-rate label from the tax_values sideload (e.g. "IVU
        // 11.5%"). One entry per applied rate; joined when there's more than
        // one. Null when the order isn't taxed (so the line just reads "Tax").
        const taxLabel = (included as any[])
          .filter((i) => i.type === 'tax_values')
          .map((tv) => {
            const a = tv.attributes || {};
            const rawPct = Number(a.percentage ?? 0);
            // Booqable returns the human percentage (e.g. 11.5); guard the rare
            // fraction form (0.115) defensively — every real PR rate is >= 1%.
            const pct = rawPct > 0 && rawPct < 1 ? rawPct * 100 : rawPct;
            return `${a.name || 'Tax'} ${+pct.toFixed(2)}%`;
          })
          .join(', ');
        setOrderTaxLabel(taxLabel || null);
        // PO number is a form field — skip on reconcile (a line edit never
        // changes it, and re-setting could clobber an in-flight edit).
        if (!isReconcile) {
          setOrderPoNumber(String(attrs.po_number ?? ''));
          setOrderPaymentTerms(String(attrs.payment_terms || DEFAULT_PAYMENT_TERMS));
          setOrderProjectName(String(attrs.project_name ?? ''));
          setIsMonthly(Boolean(attrs.is_monthly));
        }
        // Total including taxes — the field the summary label promises.
        const loadedTotalCents = Number(
          attrs.grand_total_with_tax_in_cents ??
            attrs.total_in_cents ??
            attrs.grand_total_in_cents ??
            0
        );
        setOrderTotalCents(loadedTotalCents);

        // Cache this authoritative lines/totals snapshot so the next open of the
        // same order paints the products section instantly (see the phase-1 read).
        // Refreshed on every full load AND reconcile, so it never goes stale here.
        if (orderId) {
          queryClient.setQueryData(['order-booqable-cache', orderId], {
            lines,
            subtotalCents: Number(attrs.price_in_cents ?? 0),
            taxCents: Number(attrs.tax_in_cents ?? 0),
            totalCents: loadedTotalCents,
            taxLabel: taxLabel || null,
          });
        }

        // Autosave snapshot + service location — full-load only. On reconcile
        // the customer/dates/location are unchanged, and re-arming the autosave
        // snapshot mid-edit could mask a pending save.
        if (!isReconcile) {
        // Snapshot the autosave-tracked fields directly from loaded values.
        // Doing this here (vs. reading React state) means the snapshot is
        // available synchronously before any setX-triggered re-render runs
        // the watcher useEffect — so the watcher's first post-load run sees
        // current === snapshot and skips scheduleSave.
        autosaveSnapshotRef.current = {
          customerId: customerData?.id || null,
          pickupDate: loadedPickup,
          returnDate: loadedReturn,
          poNumber: String(attrs.po_number ?? ''),
          paymentTerms: String(attrs.payment_terms || DEFAULT_PAYMENT_TERMS),
          projectName: String(attrs.project_name ?? ''),
          isMonthly: Boolean(attrs.is_monthly),
        };

        // Service location lives in TWM's DB, not Booqable — see migration
        // 030. The local mirror row (delivery_* columns) was already fetched
        // above as localPreData; reuse it instead of a second round trip.
        if (localPreData.delivery_latitude != null && localPreData.delivery_longitude != null) {
          const lat = Number(localPreData.delivery_latitude);
          const lng = Number(localPreData.delivery_longitude);
          setCurrentLocation({ lat, lng });
          setCurrentLocationAddress(localPreData.delivery_address || `${lat}, ${lng}`);
          if (localPreData.delivery_location_name) {
            setCurrentLocationName(localPreData.delivery_location_name);
          }
        }
        }

        // Phase 2 done: the products/totals section can render. (loadingOrder
        // was already cleared in phase 1; on reconcile it was never set.)
        setLoadingLines(false);
        loadedOrderIdRef.current = orderId;
        reconciledOrderIdRef.current = orderId;
      } catch (err) {
        console.error('Error fetching order data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch order');
        setLoadingOrder(false);
        setLoadingLines(false);
      }
    };

    // First load of this order runs immediately; subsequent reconciles
    // (lineRefetchTrigger bumps for the same order) are debounced so a burst of
    // quick line edits — e.g. spinning a quantity up several times — collapses
    // into a single Booqable round trip instead of one per edit. The optimistic
    // row updates already render instantly, so the brief delay only affects when
    // authoritative totals/tax settle.
    if (isNewOrder || !orderId || reconciledOrderIdRef.current !== orderId) {
      fetchOrderData();
      return;
    }
    const debounce = setTimeout(fetchOrderData, 350);
    return () => clearTimeout(debounce);
  }, [orderId, isNewOrder, lineRefetchTrigger]);

  // (Dev poll for out-of-band Booqable changes was removed — manual sync
  // button is the path. Webhooks cover production.)

  // Unified autosave for the editable order fields. Disabled on /orders/new
  // — that flow keeps an explicit Create button, since autosave-as-you-type
  // would force order creation on the very first keystroke.
  const { saveStatus, scheduleSave } = useAutoSave({
    enabled: !isNewOrder && !!orderId,
    save: async () => {
      if (!orderBooqableId) {
        // Un-promoted local draft: persist to the Supabase row (PUT /orders/:id
        // resolves either customer-id form and stores po_number). No Booqable
        // order exists yet — promotion mints it once the threshold is met.
        const response = await apiFetch(`/orders/${orderId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            starts_at: pickupDate ? new Date(`${pickupDate}T00:00:00.000Z`).toISOString() : null,
            stops_at: returnDate ? new Date(`${returnDate}T00:00:00.000Z`).toISOString() : null,
            customer_id: selectedCustomer?.id ?? null,
            po_number: orderPoNumber || '',
            payment_terms: orderPaymentTerms,
            project_name: orderProjectName || '',
            is_monthly: isMonthly,
          }),
        });
        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(`PUT /orders/${orderId} failed: HTTP ${response.status} ${text.slice(0, 200)}`);
        }
        initialCustomerId.current = selectedCustomer?.id || null;
        autosaveSnapshotRef.current = {
          customerId: selectedCustomer?.id || null,
          pickupDate,
          returnDate,
          poNumber: orderPoNumber,
          paymentTerms: orderPaymentTerms,
          projectName: orderProjectName,
          isMonthly,
        };
        return;
      }
      // Use the purpose-built backend endpoint — its connector knows that
      // `customer_id` is a JSON:API relationship while dates are attributes.
      // (Sending customer_id inside attributes via the raw /booqable-proxy
      // makes Booqable return 404 with a JSON:API "resources_not_found".)
      const updates: Record<string, unknown> = {};
      if (pickupDate) {
        updates.starts_at = new Date(`${pickupDate}T00:00:00.000Z`).toISOString();
      }
      if (returnDate) {
        updates.stops_at = new Date(`${returnDate}T00:00:00.000Z`).toISOString();
      }
      // Always include customer_id — `null` is meaningful (detach the
      // customer from this order). The connector sends it as a JSON:API
      // relationship; null clears it via `data: null`.
      updates.customer_id = selectedCustomer?.id ?? null;
      // PO number is a TWM-local field. The backend pops it before
      // forwarding to Booqable and mirrors it to orders.po_number.
      updates.po_number = orderPoNumber || '';
      // Payment terms (invoice) — likewise TWM-local; backend pops + mirrors.
      updates.payment_terms = orderPaymentTerms;
      // Project name — same TWM-local treatment (mirrored to orders.project_name).
      updates.project_name = orderProjectName || '';
      // Monthly flag — TWM-local; backend pops + mirrors to orders.is_monthly.
      updates.is_monthly = isMonthly;

      if (Object.keys(updates).length > 0) {
        const response = await apiFetch(`/booqable/orders/${orderId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates),
        });

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(`PUT /booqable/orders/${orderId} failed: HTTP ${response.status} ${text.slice(0, 200)}`);
        }
      }
      // Keep the customer-change baseline in sync so the legacy ref-guarded
      // logic elsewhere doesn't trigger a duplicate save.
      initialCustomerId.current = selectedCustomer?.id || null;

      // Update the snapshot to "what was just saved" so the watcher won't
      // keep flagging dirty on subsequent renders.
      autosaveSnapshotRef.current = {
        customerId: selectedCustomer?.id || null,
        pickupDate,
        returnDate,
        poNumber: orderPoNumber,
        paymentTerms: orderPaymentTerms,
        projectName: orderProjectName,
        isMonthly,
      };
    },
  });

  // Trigger autosave only when a tracked order field has actually drifted
  // from the loaded snapshot. The snapshot is captured by the order-load
  // handler from the loaded data (not from React state), so it's set
  // before this effect fires for the first time after hydration.
  useEffect(() => {
    if (isNewOrder || !orderId) return;
    const snap = autosaveSnapshotRef.current;
    if (!snap) return;
    const currentCustomerId = selectedCustomer?.id || null;
    const dirty =
      snap.customerId !== currentCustomerId ||
      snap.pickupDate !== pickupDate ||
      snap.returnDate !== returnDate ||
      snap.poNumber !== orderPoNumber ||
      snap.paymentTerms !== orderPaymentTerms ||
      snap.projectName !== orderProjectName ||
      snap.isMonthly !== isMonthly;
    if (!dirty) return;
    scheduleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer?.id, pickupDate, returnDate, orderPoNumber, orderPaymentTerms, orderProjectName, isMonthly]);

  // Show recalc banner when rental dates change on an order that already has lines.
  // Uses autosave snapshot as the "last saved" baseline so we don't fire on initial load.
  const prevDatesRef = useRef<{ pickup: string; return: string } | null>(null);
  useEffect(() => {
    if (isNewOrder || !orderId || !autosaveSnapshotRef.current) return;
    const prev = prevDatesRef.current;
    if (!prev) {
      prevDatesRef.current = { pickup: pickupDate, return: returnDate };
      return;
    }
    const changed = prev.pickup !== pickupDate || prev.return !== returnDate;
    // Suppress the "recalculate prices" prompt on a paid order: bulk-repricing a
    // paid order is exactly the weekly-reprice trap we steer staff away from
    // (the confirmation dialog directs them to an Extra Day line instead).
    // Also suppress it on a phased order: there the order window is the computed
    // umbrella (rollup of the phases), so a "change" here is just it tracking a
    // phase edit — and an order-wide reprice would mis-price the per-phase lines.
    // Repricing happens per phase on the phase's own window instead.
    if (changed && orderLines.length > 0 && !isInvoicePaid && orderSections.length === 0) {
      setRecalcBanner({ reason: 'period' });
    }
    prevDatesRef.current = { pickup: pickupDate, return: returnDate };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupDate, returnDate]);

  // Rental-period pickers are gated on paid orders: a date edit doesn't re-bill
  // (the paid invoice is immutable and lines carry explicit price overrides), so
  // we stash the edit and surface a confirmation dialog rather than committing it
  // silently. On an unpaid/draft order there's nothing to protect — apply directly.
  const handlePickupDateChange = useCallback(
    (value: string) => {
      if (!isNewOrder && isInvoicePaid && value !== pickupDate) {
        setPendingPeriodChange({ field: 'pickup', value });
      } else {
        setPickupDate(value);
      }
    },
    [isNewOrder, isInvoicePaid, pickupDate],
  );
  const handleReturnDateChange = useCallback(
    (value: string) => {
      if (!isNewOrder && isInvoicePaid && value !== returnDate) {
        setPendingPeriodChange({ field: 'return', value });
      } else {
        setReturnDate(value);
      }
    },
    [isNewOrder, isInvoicePaid, returnDate],
  );
  const confirmPeriodChange = useCallback(() => {
    if (!pendingPeriodChange) return;
    if (pendingPeriodChange.field === 'pickup') {
      setPickupDate(pendingPeriodChange.value);
    } else {
      setReturnDate(pendingPeriodChange.value);
    }
    setPendingPeriodChange(null);
  }, [pendingPeriodChange]);

  // --- Project-phase date chaining ----------------------------------------
  // This order is a phase when it belongs to a project. Its delivery/pickup are
  // boundaries in a contiguous swap chain, so edits cascade to the neighbors —
  // resolved locally (instant, no lag) and committed for the whole chain in one
  // batch save. The schedule entry for *this* order drives its two pickers.
  const isPhase = !!orderProject;
  // Effective lock for the project-owned fields. The override flips a phase order
  // back to free-edit (the backend PUT /orders/<id> already accepts these writes;
  // a later project-level edit will re-cascade onto *draft* phases).
  const phaseLocked = isPhase && !allowProjectOverride;
  // --- Accidental-edit guard ------------------------------------------------
  // Once an order is dispatched (started) or completed (stopped/archived) its
  // detail fields and line items render read-only by default, so a stray click
  // can't mutate a live/finished order. Deliberate edits (e.g. adding the
  // landfill overage line on a stopped order) are still possible — the user
  // hits "Edit order" to unlock for the session. The lock recomputes whenever
  // the status changes, so a revert back to reserved frees editing again.
  // Independent of (and layered under) the isInvoicePaid date-confirm dialog.
  const [editUnlocked, setEditUnlocked] = useState(false);
  useEffect(() => {
    setEditUnlocked(false);
  }, [orderId, orderStatus]);
  const completedLike =
    orderStatus === 'started' || orderStatus === 'stopped' || orderStatus === 'archived';
  const editLocked = !isNewOrder && completedLike && !editUnlocked;
  const phaseIndex = useMemo(
    () => phaseSchedule.findIndex((p) => p.id === orderId),
    [phaseSchedule, orderId],
  );
  const phaseEntry = phaseIndex >= 0 ? phaseSchedule[phaseIndex] : null;
  const prevPhasePromoted = phaseIndex > 0 && !!phaseSchedule[phaseIndex - 1]?.promoted;
  const nextPhasePromoted =
    phaseIndex >= 0 &&
    phaseIndex < phaseSchedule.length - 1 &&
    !!phaseSchedule[phaseIndex + 1]?.promoted;
  // A phase's delivery is the hinge shared with the previous phase; lock it when
  // that neighbor (or this phase) is promoted. Likewise pickup vs the next phase.
  const phasePickupDisabled = !!phaseEntry?.promoted || prevPhasePromoted;
  const phaseReturnDisabled = !!phaseEntry?.promoted || nextPhasePromoted;
  // Strict start < end calendar bounds for this phase. In absorb mode the
  // neighbours constrain too (a pick can't collapse this phase or the next);
  // ripple translates the tail so only this phase's own window needs guarding.
  const phasePrev = phaseIndex > 0 ? phaseSchedule[phaseIndex - 1] : null;
  const phaseNext =
    phaseIndex >= 0 && phaseIndex < phaseSchedule.length - 1 ? phaseSchedule[phaseIndex + 1] : null;
  const phaseDeliveryMin = phasePrev?.startDate ? addDays(phasePrev.startDate, 1) : undefined;
  const phaseDeliveryMax =
    !phaseShiftLater && phaseEntry?.endDate ? addDays(phaseEntry.endDate, -1) : undefined;
  const phasePickupMin = phaseEntry?.startDate ? addDays(phaseEntry.startDate, 1) : undefined;
  const phasePickupMax =
    !phaseShiftLater && phaseNext?.endDate ? addDays(phaseNext.endDate, -1) : undefined;
  // The project owns the service location — show the project's, not this order's
  // inherited (possibly stale) copy.
  const phaseLocation =
    orderProject?.deliveryLatitude != null && orderProject?.deliveryLongitude != null
      ? { lat: Number(orderProject.deliveryLatitude), lng: Number(orderProject.deliveryLongitude) }
      : null;
  const phaseDatesNote = orderProject
    ? `Phase ${orderProject.phaseNumber ?? '?'} of ${orderProject.phaseCount} — dates are linked to adjacent phases.${
        phaseEntry?.promoted
          ? isInvoicePaid
            ? ' This phase is paid; extend it by adding an Extra Day charge below.'
            : ` This phase is booked; reschedule it from the project schedule (P-${orderProject.projectNumber}).`
          : ''
      }`
    : undefined;

  const onPhaseDate = useCallback(
    (edge: 'start' | 'end', ymd: string) => {
      if (!orderId) return;
      const mode: CascadeMode = phaseShiftLater ? 'ripple' : 'absorb';
      setPhaseSchedule((prev) =>
        applyPhaseEdit(prev, { phaseId: orderId, edge, date: ymd || null, mode }),
      );
      setPhaseScheduleDirty(true);
      setPhaseSaveError(null);
    },
    [orderId, phaseShiftLater],
  );

  const handleSavePhaseSchedule = useCallback(async () => {
    if (!orderProject) return;
    setPhaseSaving(true);
    setPhaseSaveError(null);
    try {
      const updated = await savePhaseSchedule(
        orderProject.id,
        phaseSchedule.map((p) => ({
          order_id: p.id,
          starts_at: p.startDate ? `${p.startDate}T00:00:00.000Z` : null,
          stops_at: p.endDate ? `${p.endDate}T00:00:00.000Z` : null,
        })),
      );
      // Reseed from the persisted project (camelCase ProjectPhase shape).
      const reseeded: EditablePhase[] = updated.phases.map((p) => ({
        id: p.id,
        phaseNumber: p.phaseNumber,
        startDate: p.startDate ? p.startDate.slice(0, 10) : null,
        endDate: p.endDate ? p.endDate.slice(0, 10) : null,
        promoted: !!p.booqable_id,
      }));
      setPhaseSchedule(reseeded);
      phaseScheduleBaselineRef.current = reseeded;
      setPhaseScheduleDirty(false);
      // Sync this order's own date state (+ autosave baseline) to the saved
      // window so pricing / products and the dirty-watcher stay consistent.
      const me = updated.phases.find((p) => p.id === orderId);
      if (me) {
        const mp = me.startDate ? me.startDate.slice(0, 10) : '';
        const mr = me.endDate ? me.endDate.slice(0, 10) : '';
        setPickupDate(mp);
        setReturnDate(mr);
        if (autosaveSnapshotRef.current) {
          autosaveSnapshotRef.current = {
            ...autosaveSnapshotRef.current,
            pickupDate: mp,
            returnDate: mr,
          };
        }
      }
    } catch (e) {
      setPhaseSaveError((e as Error)?.message || 'Failed to save schedule');
    } finally {
      setPhaseSaving(false);
    }
  }, [orderProject, phaseSchedule, orderId]);

  const handleDiscardPhaseSchedule = useCallback(() => {
    setPhaseSchedule(phaseScheduleBaselineRef.current);
    setPhaseScheduleDirty(false);
    setPhaseSaveError(null);
  }, []);

  // Update header with order number
  useEffect(() => {
    if (orderNumber) {
      window.dispatchEvent(new CustomEvent('setPageContext', {
        detail: {
          title: 'Order',
          badge: `#${orderNumber}`,
        }
      }));
    } else if (isNewOrder) {
      window.dispatchEvent(new CustomEvent('setPageContext', {
        detail: {
          title: 'New Order',
        }
      }));
    }

    // Clean up on unmount
    return () => {
      window.dispatchEvent(new CustomEvent('setPageContext', { detail: null }));
    };
  }, [orderNumber, isNewOrder]);

  // Fetch the selected customer's main_address property so the billing
  // dialog's picker can offer "Customer's address" as a reference target.
  useEffect(() => {
    if (!selectedCustomer?.id) {
      setCustomerMainAddress(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/booqable/customers/${selectedCustomer.id}`);
        if (!res.ok || cancelled) return;
        const json = await res.json();
        const included = json.included || [];
        const mainAddr = included.find(
          (item: any) =>
            item.type === 'properties' &&
            item.attributes?.identifier === 'main_address',
        );
        if (cancelled) return;

        // Compare the selected customer's tax profile against what the order
        // was last taxed under. On initial load these match (the order's tax
        // region was derived from this same customer), so this only fires when
        // the user swaps in a customer with a different profile — and only when
        // there are lines to reprice.
        const custTaxRegion =
          json.data?.attributes?.tax_region_id ||
          json.data?.relationships?.tax_region?.data?.id ||
          null;
        customerTaxRegionRef.current = custTaxRegion;
        if (
          !isNewOrder &&
          orderId &&
          orderLines.length > 0 &&
          custTaxRegion !== orderTaxRegionRef.current
        ) {
          setRecalcBanner({ reason: 'tax' });
        }
        if (mainAddr) {
          const a = mainAddr.attributes || {};
          const display = [a.address1, a.address2, a.city, a.zipcode, a.region, a.country]
            .filter(Boolean)
            .join(', ');
          setCustomerMainAddress({
            id: mainAddr.id,
            display,
            fields: {
              address1: a.address1 || '',
              address2: a.address2 || '',
              city: a.city || '',
              region: a.region || '',
              zipcode: a.zipcode || '',
              country: a.country || '',
            },
          });

          // Default the order's billing address to the customer's saved
          // address when the order doesn't already bill to its own. Without
          // this the saved address was only pre-selected inside the dialog;
          // staff expect it to show on the order itself (and feed the payment
          // billTo). Guard on billingAddressIdRef so we never clobber an order
          // that already has its own distinct billing_address property. The
          // order-load effect sets that ref synchronously before this async
          // fetch resolves, so the guard is reliable.
          const hasOwnBilling =
            !!billingAddressIdRef.current &&
            billingAddressIdRef.current !== mainAddr.id;
          if (!hasOwnBilling) {
            billingAddressIdRef.current = mainAddr.id;
            setBillingMode('customer');
            setBillingAddressLine1(a.address1 || '');
            setBillingAddressLine2(a.address2 || '');
            setBillingCity(a.city || '');
            setBillingRegion(a.region || '');
            setBillingPostalCode(a.zipcode || '');
            setBillingCountry(a.country || '');
            setSavedBillingAddress(display);
            savedBillingValueRef.current = display;
          }
        } else {
          setCustomerMainAddress(null);
        }
      } catch (err) {
        console.error('[customer-main-address] fetch failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCustomer?.id]);

  // Open the email-history dialog when the OrderRightSidebar dispatches the event.
  useEffect(() => {
    const open = () => setEmailHistoryOpen(true);
    window.addEventListener('viewOrderEmailHistory', open);
    return () => window.removeEventListener('viewOrderEmailHistory', open);
  }, []);

  // Open a recorded email's rendered body in a new tab. Mirrors the attachment
  // preview flow: open the tab synchronously inside the click (so popup
  // blockers don't kill it), then navigate it to the authed blob once fetched.
  const handleViewSentEmail = useCallback(async (sendId: string) => {
    const tab = window.open('about:blank', '_blank');
    try {
      const res = await apiFetch(`/emails/sends/${encodeURIComponent(sendId)}/preview`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Preview failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (tab) tab.location.href = url;
      else window.open(url, '_blank');
    } catch (e: any) {
      if (tab) tab.close();
      dispatchToast(
        <Toast><ToastTitle>Could not open email</ToastTitle>
          <ToastBody>{e?.message || 'The rendered email is unavailable.'}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    }
  }, [dispatchToast]);

  // Load the order's email history whenever the dialog opens for a promoted
  // order. Sends are keyed on the Booqable order id (same id the Send-Email
  // dialog records against), so un-promoted drafts have nothing to show.
  useEffect(() => {
    if (!emailHistoryOpen) return;
    if (!orderBooqableId) { setEmailHistoryEntries([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(
          `/emails/history?entity_type=order&entity_id=${encodeURIComponent(orderBooqableId)}`,
        );
        if (!res.ok) return;
        const json = await res.json();
        if (cancelled) return;
        const entries: EmailHistoryEntry[] = (json.data || []).map((r: any) => ({
          id: r.id,
          sentAt: r.sentAt,
          senderName: r.senderName || 'TWM',
          subject: r.subject || '(no subject)',
          status: (KNOWN_EMAIL_STATUSES.includes(r.status) ? r.status : 'sent') as EmailDeliveryStatus,
          recipients: r.to ? String(r.to).split(',').map((s: string) => s.trim()).filter(Boolean) : undefined,
          onViewEmail: r.hasBody ? () => handleViewSentEmail(r.id) : undefined,
        }));
        setEmailHistoryEntries(entries);
      } catch {
        // Non-fatal — the dialog just shows its empty state.
      }
    })();
    return () => { cancelled = true; };
  }, [emailHistoryOpen, orderBooqableId, handleViewSentEmail]);

  // Customer changed — clear the saved-locations dropdown index, which
  // referred to the previous customer's list. The order's delivery
  // location is NOT touched: it's a property of the order, persists in the
  // backend independent of which customer is attached, and clearing it
  // on-screen only created a misleading divergence (the pin reappeared on
  // refresh). See #205 (original behavior) and the customer-removal fix.
  useEffect(() => {
    setSelectedLocationIndex(null);
  }, [selectedCustomer?.id]);

  // Suppress ResizeObserver errors (common with Mapbox GL and Fluent UI)
  useEffect(() => {
    const resizeObserverErrorHandler = (e: ErrorEvent) => {
      if (e.message && e.message.includes('ResizeObserver loop')) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
    };

    // Also suppress via window.onerror
    const originalOnError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      if (typeof message === 'string' && message.includes('ResizeObserver loop')) {
        return true; // Suppress error
      }
      if (originalOnError) {
        return originalOnError(message, source, lineno, colno, error);
      }
      return false;
    };

    window.addEventListener('error', resizeObserverErrorHandler);

    return () => {
      window.removeEventListener('error', resizeObserverErrorHandler);
      window.onerror = originalOnError;
    };
  }, []);

  // Close context menu on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && contextMenu) {
        setContextMenu(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu]);

  // Initialize map when container is available (after loading completes)
  useEffect(() => {
    if (loadingOrder) return; // Wait for loading to complete
    if (!mapContainer.current) return;
    if (map.current) return; // Don't re-initialize if map exists

    // Create new map - default to Puerto Rico
    const defaultCenter: [number, number] = [-66.5901, 18.2208];

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: defaultCenter,
      zoom: 8,
    });

    // Ensure map resizes properly after load
    map.current.on('load', () => {
      map.current?.resize();
    });

    // Add right-click context menu handler to map
    map.current.on('contextmenu', (e) => {
      e.preventDefault();
      const newLat = parseFloat(e.lngLat.lat.toFixed(6));
      const newLng = parseFloat(e.lngLat.lng.toFixed(6));

      // Get the map container's position to calculate absolute position
      const mapContainer = map.current!.getContainer();
      const rect = mapContainer.getBoundingClientRect();

      setContextMenu({
        x: e.point.x + rect.left,
        y: e.point.y + rect.top,
        lat: newLat,
        lng: newLng,
      });
    });

    // Close context menu on any click
    map.current.on('click', () => {
      setContextMenu(null);
    });

    return () => {
      if (marker.current) {
        marker.current.remove();
        marker.current = null;
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [loadingOrder]); // Re-run when loading completes

  // Update map when location changes (separate effect)
  useEffect(() => {
    if (!map.current || !currentLocation) return;

    // Validate coordinates before updating map
    const { lat, lng } = currentLocation;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      console.error('Invalid coordinates:', lat, lng);
      return;
    }

    // Update map center
    map.current.flyTo({
      center: [lng, lat],
      zoom: 15,
      essential: true,
    });

    // Update or create marker
    if (marker.current) {
      marker.current.setLngLat([lng, lat]);
    } else {
      marker.current = new mapboxgl.Marker({ draggable: true })
        .setLngLat([lng, lat])
        .addTo(map.current);

      // Handle marker drag
      marker.current.on('dragend', () => {
        const lngLat = marker.current!.getLngLat();
        const newLat = parseFloat(lngLat.lat.toFixed(6));
        const newLng = parseFloat(lngLat.lng.toFixed(6));
        setCurrentLocation({ lat: newLat, lng: newLng });
        setCurrentLocationAddress(`${newLat}, ${newLng}`);
        setSelectedLocationIndex(null);
      });
    }
  }, [currentLocation]);

  // Tracks in-flight service-address → municipality resolutions (geocode +
  // /rolloff-rates/lookup). A counter, not a boolean, because the pre-promotion
  // effect and the promote/save chains can overlap — the picker stays gated
  // until the last one settles. Gates the product picker (with tierResolving)
  // so a fast user can't add a line before its tier-derived price lands.
  const [municipalityResolveCount, setMunicipalityResolveCount] = useState(0);
  const municipalityResolving = municipalityResolveCount > 0;

  // reverseGeocode coordinates → /rolloff-rates/lookup → municipality_id.
  // Returns the id (or null on no-match / network error). Side-effect free:
  // callers decide whether to persist it. See #150 R7.
  const lookupMunicipalityId = useCallback(
    async (lat: number, lng: number): Promise<string | null> => {
      setMunicipalityResolveCount((n) => n + 1);
      try {
        const municipalityName = await getGeocodeConnector().reverseGeocodeMunicipality(lat, lng);
        if (!municipalityName) {
          console.warn('[municipality] reverseGeocode returned no PR municipio for', lat, lng);
          return null;
        }
        const lookupRes = await apiFetch(
          `/rolloff-rates/lookup?municipality=${encodeURIComponent(municipalityName)}`,
        );
        if (!lookupRes.ok) {
          console.warn('[municipality] /rolloff-rates/lookup failed:', lookupRes.status);
          return null;
        }
        const lookupBody = await lookupRes.json();
        const municipalityId = lookupBody?.municipality?.id;
        if (!municipalityId) {
          console.warn('[municipality] lookup returned no municipality.id:', lookupBody);
          return null;
        }
        return municipalityId;
      } catch (lookupErr) {
        console.warn('[municipality] lookup chain failed:', lookupErr);
        return null;
      } finally {
        setMunicipalityResolveCount((n) => Math.max(0, n - 1));
      }
    },
    [],
  );

  // Resolve a saved order's service-address municipality and persist
  // municipality_id (PUT /orders/:id) so /pricing/resolve can derive the tier.
  // Returns whether it landed. Failures are non-fatal — the location save
  // itself is separate; the tier badge just stays "Capture service address".
  const resolveOrderMunicipality = useCallback(
    async (targetOrderId: string, lat: number, lng: number): Promise<boolean> => {
      const municipalityId = await lookupMunicipalityId(lat, lng);
      if (!municipalityId) return false;
      try {
        const putMuni = await apiFetch(`/orders/${targetOrderId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ municipality_id: municipalityId }),
        });
        if (!putMuni.ok) {
          console.warn('[municipality] PUT municipality_id failed:', putMuni.status);
          return false;
        }
        return true;
      } catch (resolveErr) {
        console.warn('[municipality] PUT municipality_id failed:', resolveErr);
        return false;
      }
    },
    [lookupMunicipalityId],
  );

  // Before promotion, resolve the service-address municipality as soon as a
  // location is picked and cache it. Promotion sends it so the product picker's
  // tier badge resolves immediately instead of after a post-promote geocode
  // round-trip. Promoted orders resolve via handleSaveServiceLocation.
  const [pendingMunicipalityId, setPendingMunicipalityId] = useState<string | null>(null);
  const pendingMuniCoordsRef = useRef<{ lat: number; lng: number } | null>(null);
  useEffect(() => {
    // Runs until promotion (covers /orders/new and the un-promoted local row);
    // promoted orders resolve municipality through handleSaveServiceLocation.
    if (isPromoted) return;
    if (!currentLocation) {
      setPendingMunicipalityId(null);
      pendingMuniCoordsRef.current = null;
      return;
    }
    const { lat, lng } = currentLocation;
    const prev = pendingMuniCoordsRef.current;
    if (prev && prev.lat === lat && prev.lng === lng) return;
    let cancelled = false;
    lookupMunicipalityId(lat, lng).then((id) => {
      if (cancelled) return;
      pendingMuniCoordsRef.current = { lat, lng };
      setPendingMunicipalityId(id);
    });
    return () => { cancelled = true; };
  }, [isPromoted, currentLocation, lookupMunicipalityId]);

  // Create a Supabase-only draft (no Booqable order yet) for the new-order
  // flow. Auto-fired once a customer is selected (see effect below) — there's
  // no manual button. POSTs /orders/local-draft, then replaces the URL with
  // /orders/:id; from there the order's autosave persists subsequent edits and
  // the order is promoted to Booqable on minimal-info / first line add. Clears
  // the scratchpad on success. Only the customer is required.
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftCreateError, setDraftCreateError] = useState(false);
  const localDraftStartedRef = useRef(false);
  const createLocalDraft = useCallback(async () => {
    if (!selectedCustomer) return;
    if (savingDraft) return;
    setSavingDraft(true);
    setDraftCreateError(false);
    try {
      const response = await apiFetch('/orders/local-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: selectedCustomer.id,
          pickup_date: pickupDate || null,
          return_date: returnDate || null,
          delivery_address: currentLocationAddress || null,
          delivery_latitude: currentLocation?.lat ?? null,
          delivery_longitude: currentLocation?.lng ?? null,
          delivery_location_name: currentLocationName || null,
          municipality_id: pendingMunicipalityId,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Failed to create draft (${response.status})`);
      }
      const data = await response.json();
      if (!data.order_id) {
        throw new Error('Draft created but no order_id returned');
      }
      clearOrderDraftScratch();
      // Pre-arm so the post-navigate load skips the spinner — the form already
      // shows everything the user just entered; the load just confirms it from
      // the local row and arms autosave.
      loadedOrderIdRef.current = data.order_id;
      setSavingDraft(false);
      navigate(`/orders/${data.order_id}`, { replace: true });
    } catch (err: any) {
      console.error('Error creating local draft:', err);
      dispatchToast(
        <Toast>
          <ToastTitle>Couldn't start order</ToastTitle>
          <ToastBody>{err?.message || 'Unknown error — try again or contact support.'}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
      setSavingDraft(false);
      setDraftCreateError(true);
      localDraftStartedRef.current = false;
    }
  }, [
    selectedCustomer,
    pickupDate,
    returnDate,
    currentLocationAddress,
    currentLocation?.lat,
    currentLocation?.lng,
    currentLocationName,
    pendingMunicipalityId,
    savingDraft,
    navigate,
    dispatchToast,
  ]);

  // Auto-create the local draft once a customer is selected on /orders/new —
  // the user never clicks "save". Debounced ~1s so dates/location picked right
  // after the customer fold into the initial POST. Fires exactly once
  // (localDraftStartedRef); createLocalDraft is read through a ref so editing
  // other fields during the debounce doesn't restart the timer — the trigger
  // is the customer.
  const createLocalDraftRef = useRef(createLocalDraft);
  useEffect(() => { createLocalDraftRef.current = createLocalDraft; }, [createLocalDraft]);
  // Re-arm the one-shot guard whenever the route returns to /orders/new
  // (the component instance is reused across /orders/:id ↔ /orders/new), so a
  // second new order in the same session still auto-creates its draft.
  useEffect(() => {
    if (isNewOrder) localDraftStartedRef.current = false;
  }, [isNewOrder]);
  useEffect(() => {
    if (!isNewOrder || !selectedCustomer) return;
    if (localDraftStartedRef.current || savingDraft) return;
    const t = setTimeout(() => {
      if (localDraftStartedRef.current || savingDraft) return;
      localDraftStartedRef.current = true;
      createLocalDraftRef.current();
    }, 1000);
    return () => clearTimeout(t);
  }, [isNewOrder, selectedCustomer?.id, savingDraft]);

  // Promote the local draft to a real Booqable order (idempotent). Sends the
  // current field values so the backend promotes with the latest data
  // regardless of autosave timing. On success, flips isPromoted (via
  // orderBooqableId) which unlocks every Booqable-touching feature (the product
  // picker, transitions, billing, invoices). Returns the booqable id (or null).
  const promoteOrder = useCallback(async (): Promise<string | null> => {
    if (!orderId || isNewOrder) return null;
    if (orderBooqableId) return orderBooqableId;
    if (promotionStartedRef.current) return null;
    promotionStartedRef.current = true;
    try {
      const res = await apiFetch(`/orders/${orderId}/promote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pickup_date: pickupDate || null,
          return_date: returnDate || null,
          delivery_address: currentLocationAddress || null,
          delivery_latitude: currentLocation?.lat ?? null,
          delivery_longitude: currentLocation?.lng ?? null,
          delivery_location_name: currentLocationName || null,
          municipality_id: pendingMunicipalityId,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Promote failed (${res.status})`);
      }
      const data = await res.json();
      if (!data.booqable_id) throw new Error('Promote returned no booqable_id');
      setOrderBooqableId(data.booqable_id);
      if (data.number) setOrderNumber(String(data.number));
      // Fallback: a location was captured but its municipality_id wasn't ready
      // at promote time (geocode still in flight). Resolve it now so the tier
      // badge still populates. Otherwise the persisted municipality is enough.
      if (currentLocation && !pendingMunicipalityId) {
        const { lat, lng } = currentLocation;
        resolveOrderMunicipality(orderId, lat, lng).then((resolved) => {
          if (resolved) refetchTier();
        });
      } else {
        refetchTier();
      }
      return data.booqable_id;
    } catch (err: any) {
      console.error('Error promoting order:', err);
      promotionStartedRef.current = false;
      dispatchToast(
        <Toast>
          <ToastTitle>Couldn't finalize order</ToastTitle>
          <ToastBody>{err?.message || 'Unknown error — try again or contact support.'}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
      return null;
    }
  }, [
    orderId,
    isNewOrder,
    orderBooqableId,
    pickupDate,
    returnDate,
    currentLocationAddress,
    currentLocation?.lat,
    currentLocation?.lng,
    currentLocationName,
    currentLocation,
    pendingMunicipalityId,
    refetchTier,
    resolveOrderMunicipality,
    dispatchToast,
  ]);

  // Promote as soon as the draft has a customer. A Booqable order is
  // structurally required to add lines (it must belong to a customer), but
  // dates and a service location are NOT — the backend promote creates a
  // periodless/locationless order fine (Reserve is what gates on dates), and
  // lines added before a location resolves simply come in unpriced (the
  // per-line "Select a service location to get proper pricing" hint covers it).
  // So the product picker unlocks on customer alone; staff add items first and
  // fill in dates/location in any order. Skips once promoted / in flight.
  useEffect(() => {
    if (isNewOrder || !orderId) return;
    if (orderBooqableId || promotionStartedRef.current) return;
    if (selectedCustomer) {
      promoteOrder();
    }
  }, [
    isNewOrder,
    orderId,
    orderBooqableId,
    selectedCustomer,
    promoteOrder,
  ]);

  // The product picker is blocked until the order is promoted (a real Booqable
  // order is required to add lines). Promotion fires on customer-select above,
  // so by the time the picker unlocks the order exists — dates/location are no
  // longer prerequisites.

  const handleSaveServiceLocation = useCallback(async (
    overrideLocation?: { lat: number; lng: number },
    overrideLabel?: string
  ) => {
    const location = overrideLocation || currentLocation;
    const rawName = overrideLabel ?? currentLocationName;

    if (!location) {
      alert('Please select or enter coordinates.');
      return;
    }
    if (!orderId || isNewOrder) {
      // Save defers to order creation for new orders.
      return;
    }

    const { lat, lng } = location;
    const name = rawName.trim();
    const deliveryAddress =
      currentLocationAddress || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

    try {
      const response = await apiFetch(`/orders/${orderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          delivery_address: deliveryAddress,
          delivery_latitude: lat,
          delivery_longitude: lng,
          delivery_location_name: name || null,
        }),
      });
      if (!response.ok) {
        throw new Error(`Failed to save delivery location: ${response.statusText}`);
      }

      // Resolve municipality + tier so /pricing/resolve stops returning
      // source='unresolved' (reverseGeocode → /rolloff-rates/lookup → PUT
      // municipality_id, via resolveOrderMunicipality so the in-flight count
      // gates the product picker). Failures are non-fatal: the location save
      // itself succeeded; the tier badge just stays "Capture service address"
      // until the next attempt or a manual recapture. See #150 R7.
      const resolved = await resolveOrderMunicipality(orderId, lat, lng);
      if (resolved) {
        refetchTier();
        if (orderLines.length > 0) {
          // A line added before any location existed can't be tier-priced, so
          // it sits at a $0 charge with a "select a location" prompt. Such a
          // line has no manual override worth preserving — so once a location
          // resolves, recalc automatically and suppress the banner. If ANY
          // line already carries a nonzero charge (a possible manual override),
          // fall back to the prompt so a recalc can't silently overwrite it.
          const allLinesUnpriced = orderLines.every((l) => !l.totalInCents);
          if (allLinesUnpriced) {
            const recalcRes = await apiFetch(
              `/orders/${encodeURIComponent(orderId)}/recalculate-prices`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pickup_date: pickupDate, return_date: returnDate }),
              },
            );
            if (recalcRes.ok) {
              // Recalc re-stamps the order's tax region to the customer's, so
              // advance the baseline to match (see handleRecalculatePrices).
              orderTaxRegionRef.current = customerTaxRegionRef.current;
              setLineRefetchTrigger((c) => c + 1);
              refreshOrderInvoices();
            } else {
              // Auto-recalc failed — fall back to the manual prompt so staff
              // can retry rather than being left with a silently-stale $0 line.
              setRecalcBanner({ reason: 'location' });
            }
          } else {
            setRecalcBanner({ reason: 'location' });
          }
        }
      }
    } catch (err) {
      console.error('Error saving delivery location:', err);
      alert('Error saving delivery location');
    }
  }, [currentLocation, currentLocationName, currentLocationAddress, orderId, isNewOrder, pickupDate, returnDate, refetchTier, resolveOrderMunicipality, orderLines, refreshOrderInvoices]);

  // Persist the current pin as a saved location on the selected customer.
  // Writes to the customer_locations table via POST /customers/:id/locations.
  // TanStack invalidation re-fetches the list so customerLocations updates.
  const handleSaveLocationToCustomerWithLabel = useCallback(async (rawLabel: string) => {
    if (!selectedCustomer) {
      alert('Please select a customer first.');
      return;
    }
    if (!currentLocation) {
      alert('Please select or enter coordinates.');
      return;
    }

    // Default label so users can save with one click after dropping a pin.
    const label = rawLabel.trim() || 'Service Location';
    const { lat, lng } = currentLocation;

    const existingLabelLocation = customerLocations.find(
      loc => loc.label?.toLowerCase() === label.toLowerCase(),
    );
    if (existingLabelLocation) {
      const confirmed = window.confirm(
        `A location with the label "${label}" already exists for this customer. Save anyway?`,
      );
      if (!confirmed) return;
    }

    const nearbyLocation = customerLocations.find(loc => {
      const distance = calculateDistance(lat, lng, loc.lat, loc.lng);
      return distance <= 20;
    });
    if (nearbyLocation) {
      alert(`This location is within 20 meters of an existing location: "${nearbyLocation.label || 'Unlabeled'}"`);
      return;
    }

    try {
      await addCustomerLocation(selectedCustomer.id, {
        name: label,
        address: currentLocationAddress || null,
        latitude: lat,
        longitude: lng,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.customers.locations(selectedCustomer.id),
      });
    } catch (err) {
      console.error('Error saving location to customer:', err);
      alert(`Error saving location to customer: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [selectedCustomer, currentLocation, customerLocations, currentLocationAddress, queryClient]);

  // Stable callbacks handed to the (memoized) LocationSection so unrelated
  // field edits don't re-render the location picker. Each captures the latest
  // state through its dependency list.
  const handleLocationPickerSelect = useCallback(
    ({ lat, lng, address, name }: { lat: number; lng: number; address?: string; name?: string }) => {
      setCurrentLocation({ lat, lng });
      setCurrentLocationAddress(address || `${lat}, ${lng}`);
      setCurrentLocationName(name ?? '');
      setSelectedLocationIndex(null);
    },
    [],
  );

  const handleSaveLocationToCustomerClick = useCallback(() => {
    if (!currentLocation) {
      alert('Please select or enter coordinates first.');
      return;
    }
    handleSaveLocationToCustomerWithLabel(currentLocationName || '');
  }, [currentLocation, currentLocationName, handleSaveLocationToCustomerWithLabel]);

  const handleAddLocationToOrder = useCallback(() => {
    handleSaveServiceLocation();
  }, [handleSaveServiceLocation]);

  const handleRemoveLocation = useCallback(() => {
    setCurrentLocation(null);
    setCurrentLocationAddress('');
    setCurrentLocationName('');
    setSelectedLocationIndex(null);
    // The active tier follows the service address — drop it so the product
    // picker stops showing the removed location's rate tier.
    clearTier();
    // Persist the clear when editing an existing order. New orders just drop
    // the local state — nothing to save yet.
    if (!isNewOrder && orderId) {
      apiFetch(`/orders/${orderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          delivery_address: null,
          delivery_latitude: null,
          delivery_longitude: null,
          delivery_location_name: null,
        }),
      }).catch((err) => console.error('Failed to clear delivery location:', err));
    }
  }, [isNewOrder, orderId, clearTier]);

  // Fetch customer fields when customer is selected
  useEffect(() => {
    let cancelled = false;

    const fetchCustomerFields = async () => {
      if (!selectedCustomer) {
        setCustomerFields([]);
        return;
      }

      try {
        setLoadingFields(true);
        const response = await apiFetch(`/booqable/customers/${selectedCustomer.id}`);

        if (cancelled) return;

        if (!response.ok) {
          throw new Error(`Failed to fetch customer details: ${response.statusText}`);
        }

        const result = await response.json();

        if (cancelled) return;

        const customerData = result.data;
        const attrs = customerData.attributes || {};

        // Extract properties (custom fields)
        const properties = attrs.properties || {};
        const fields: CustomerField[] = Object.entries(properties)
          .filter(([key, value]) => value && key !== 'phone' && key !== 'locations') // Exclude phone and locations
          .map(([key, value]) => ({
            identifier: key,
            value: String(value),
            label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
          }));

        setCustomerFields(fields);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching customer fields:', err);
        setCustomerFields([]);
      } finally {
        if (!cancelled) {
          setLoadingFields(false);
        }
      }
    };

    fetchCustomerFields();

    return () => {
      cancelled = true;
    };
  }, [selectedCustomer]);

  const performCustomerClear = useCallback(() => {
    setSelectedCustomer(null);
    setCustomerFields([]);
    setSelectedLocationIndex(null);
    // The billing address belongs to the customer (it's either their saved
    // address pre-populated onto the order, or one entered for them), so clear
    // it along with the customer — otherwise it lingers on a now-customerless
    // order. Resets the on-page display, the dialog form fields, and the
    // reference state back to a blank "new address".
    setCustomerMainAddress(null);
    setSavedBillingAddress(null);
    savedBillingValueRef.current = null;
    billingAddressIdRef.current = null;
    setBillingMode('new');
    setBillingCountry('United States');
    setBillingAddressLine1('');
    setBillingAddressLine2('');
    setBillingCity('');
    setBillingRegion('');
    setBillingPostalCode('');
    setBillingSaveError(null);
  }, []);

  const handleCustomerClear = useCallback(() => {
    if (!isNewOrder && selectedCustomer) {
      setRemoveCustomerDialogOpen(true);
      return;
    }

    performCustomerClear();
  }, [isNewOrder, selectedCustomer, performCustomerClear]);

  // Wipe every new-order field back to its pristine default. The /orders/new
  // and /orders/:id routes share a single OrderDetails instance (see App.tsx)
  // so the draft-save nav doesn't remount/flash — but that means clicking
  // "New Order" from an existing order lands on /orders/new with the previous
  // order's state still in place. Left as-is, the stale selectedCustomer keeps
  // the customer card mounted (the search box never reappears) and the
  // auto-create-draft effect fires for that stale customer, spinning up a
  // duplicate order before you can pick anyone. See the transition effect below.
  const resetNewOrderForm = useCallback(() => {
    performCustomerClear();
    setPickupDate('');
    setReturnDate('');
    setCurrentLocation(null);
    setCurrentLocationAddress('');
    setCurrentLocationName('');
    setSelectedLocationIndex(null);
    setOrderLines([]);
    setOrderNumber(null);
    setOrderStatus(null);
    setOrderPoNumber('');
    setOrderPaymentTerms(DEFAULT_PAYMENT_TERMS);
    setOrderProjectName('');
    setIsMonthly(false);
    setOrderBooqableId(null);
    setOrderSubtotalCents(0);
    setOrderTaxCents(0);
    setOrderTaxLabel(null);
    setOrderTotalCents(0);
    setRecalcBanner(null);
    setError(null);
    setSavedAddressOption('new');
    setPendingMunicipalityId(null);
    clearTier();
    initialCustomerId.current = undefined;
    loadedOrderIdRef.current = null;
    reconciledOrderIdRef.current = null;
    autosaveSnapshotRef.current = null;
    promotionStartedRef.current = false;
    localDraftStartedRef.current = false;
    orderTaxRegionRef.current = null;
    customerTaxRegionRef.current = null;
  }, [performCustomerClear, clearTier]);

  // Reset only on the existing-order → new-order transition. The initial mount
  // (was === is) is left untouched so the scratchpad-restore effect can rehydrate
  // a genuine /orders/new visit; the draft-save nav (new → existing) is a no-op.
  const prevIsNewOrderRef = useRef(isNewOrder);
  useEffect(() => {
    const wasNewOrder = prevIsNewOrderRef.current;
    prevIsNewOrderRef.current = isNewOrder;
    if (isNewOrder && !wasNewOrder) {
      resetNewOrderForm();
    }
  }, [isNewOrder, resetNewOrderForm]);

  const handleSavedAddressChange = (value: string) => {
    setSavedAddressOption(value);
    if (value === 'new') {
      // Clear all fields for new address
      setBillingCountry('United States');
      setBillingAddressLine1('');
      setBillingAddressLine2('');
      setBillingCity('');
      setBillingRegion('');
      setBillingPostalCode('');
    } else if (selectedCustomer) {
      // Populate with selected customer's address
      setBillingCountry(selectedCustomer.country || '');
      setBillingAddressLine1(selectedCustomer.address || '');
      setBillingAddressLine2('');
      setBillingCity(selectedCustomer.city || '');
      setBillingRegion(selectedCustomer.municipality || '');
      setBillingPostalCode(selectedCustomer.zipCode || '');
    }
  };

  const formatCustomerAddress = (customer: Customer) => {
    const parts = [];
    if (customer.address) parts.push(customer.address);
    if (customer.city) parts.push(customer.city);
    if (customer.zipCode) parts.push(customer.zipCode);
    if (customer.municipality) parts.push(customer.municipality);
    if (customer.country) parts.push(customer.country);
    return parts.join(', ') || 'No address';
  };

  const handleSaveBillingAddress = async () => {
    setBillingSaveError(null);

    // No Booqable order yet (/orders/new or an un-promoted local draft) —
    // keep the local-only display behavior so the user can fill the form
    // before the order is promoted. Billing properties are order-owned in
    // Booqable, so they can't be created until promotion mints the order.
    if (!isPromoted || !orderId) {
      const addressParts = [billingAddressLine1];
      if (billingAddressLine2) addressParts.push(billingAddressLine2);
      addressParts.push(billingCity);
      addressParts.push(billingPostalCode);
      if (billingRegion) addressParts.push(billingRegion);
      addressParts.push(billingCountry);
      setSavedBillingAddress(addressParts.join(', '));
      setBillingAddressDialogOpen(false);
      return;
    }

    // Always send inline mode. Booqable's "use customer's saved address"
    // UX is implemented by *copying* the customer's address values into a
    // new order-owned billing_address property — Booqable enforces that
    // billing addresses must be owned by the order, not by a customer
    // (422 invalid_billing_address_property_owner). The form fields already
    // hold the customer's values when billingMode === 'customer' (set by
    // the picker / handleChangeBillingAddress), so we just send what's in
    // the form regardless of mode.
    if (!billingAddressLine1 || !billingCity || !billingPostalCode) {
      setBillingSaveError('Address line 1, city, and postal code are required.');
      return;
    }
    const body: Record<string, unknown> = {
      mode: 'inline',
      address: {
        address1: billingAddressLine1,
        address2: billingAddressLine2,
        city: billingCity,
        region: billingRegion,
        zipcode: billingPostalCode,
        country: billingCountry,
      },
    };
    // Update-in-place when the order already had its own billing property.
    // Skip when the existing id is the customer's main_address — that
    // means the order is currently in "reference customer" mode (rare,
    // legacy data) and we want to create a fresh order-level row instead
    // of clobbering the customer's address.
    const isUpdate =
      !!billingAddressIdRef.current &&
      billingAddressIdRef.current !== customerMainAddress?.id;
    if (isUpdate) {
      body.existing_property_id = billingAddressIdRef.current!;
    }
    // Provenance breadcrumb: only valid on CREATE. Booqable rejects updates
    // that include `default_property_id` (`default property cannot be
    // changed`). When creating a new billing property from the customer's
    // saved address, attach the customer's main_address id as the source.
    if (!isUpdate && billingMode === 'customer' && customerMainAddress) {
      (body.address as Record<string, unknown>).default_property_id =
        customerMainAddress.id;
    }
    const displayParts = [
      billingAddressLine1,
      billingAddressLine2,
      billingCity,
      billingPostalCode,
      billingRegion,
      billingCountry,
    ].filter(Boolean);

    setSavingBillingAddress(true);
    try {
      const res = await apiFetch(`/booqable/orders/${orderId}/billing-address`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${text.slice(0, 200)}`);
      }
      // Refresh billingAddressIdRef from the response — Booqable returns the
      // updated order including the (possibly new) billing_address_property_id
      // and the composed `value` string. Sync the value ref so the dev poll
      // doesn't immediately flag a "drift" on its next tick from format
      // differences between our locally-joined display and Booqable's.
      const data = await res.json().catch(() => null);
      const newPropertyId = data?.data?.attributes?.billing_address_property_id;
      if (newPropertyId) billingAddressIdRef.current = newPropertyId;
      const remoteBillingProp = (data?.included || []).find(
        (item: any) =>
          item.type === 'properties' &&
          (item.id === newPropertyId || item.attributes?.identifier === 'billing_address'),
      );
      const remoteValue = remoteBillingProp?.attributes?.value;
      const display = remoteValue || displayParts.join(', ');
      setSavedBillingAddress(display);
      savedBillingValueRef.current = display;
      setBillingAddressDialogOpen(false);
    } catch (err) {
      console.error('[billing-address save] failed:', err);
      setBillingSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSavingBillingAddress(false);
    }
  };

  const handleChangeBillingAddress = useCallback(() => {
    // Derive the initial mode: if the order's billing_address_property_id
    // points at the customer's main_address, the order is currently using
    // the customer's saved address. Otherwise it has its own (or none).
    if (
      customerMainAddress &&
      billingAddressIdRef.current === customerMainAddress.id
    ) {
      setBillingMode('customer');
      // Mirror customer's address into the (read-only) form so the user
      // can see what they're picking. Form fields aren't populated by the
      // order load in reference mode since the customer's main_address
      // isn't in the order's included array.
      const f = customerMainAddress.fields;
      setBillingAddressLine1(f.address1);
      setBillingAddressLine2(f.address2);
      setBillingCity(f.city);
      setBillingRegion(f.region);
      setBillingPostalCode(f.zipcode);
      setBillingCountry(f.country);
    } else {
      setBillingMode('new');
    }
    setBillingSaveError(null);
    setBillingAddressDialogOpen(true);
  }, [customerMainAddress]);

  const handleAddBillingAddress = useCallback(() => {
    // Pre-populate from the customer's saved address when they have one, so
    // the operator can accept it in one click instead of retyping. Falls back
    // to a blank "new address" form when the customer has no main_address.
    if (customerMainAddress) {
      setBillingMode('customer');
      const f = customerMainAddress.fields;
      setBillingAddressLine1(f.address1);
      setBillingAddressLine2(f.address2);
      setBillingCity(f.city);
      setBillingRegion(f.region);
      setBillingPostalCode(f.zipcode);
      setBillingCountry(f.country);
    } else {
      setBillingMode('new');
    }
    setBillingSaveError(null);
    setBillingAddressDialogOpen(true);
  }, [customerMainAddress]);
  const handleRemoveBillingAddress = useCallback(() => setSavedBillingAddress(null), []);

  // Handle selecting location from dropdown
  const handleLocationSelect = useCallback((index: number) => {
    const location = customerLocations[index];
    if (location) {
      setCurrentLocation({ lat: location.lat, lng: location.lng });
      setSelectedLocationIndex(index);
      setCurrentLocationAddress(location.address || `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`);
      setCurrentLocationName(location.label || '');

      // Auto-save if editing existing order and the saved location has a label
      if (!isNewOrder && orderId && location.label) {
        handleSaveServiceLocation(
          { lat: location.lat, lng: location.lng },
          location.label
        );
      }
    }
  }, [customerLocations, isNewOrder, orderId, handleSaveServiceLocation]);

  // Handle setting location from context menu
  const handleSetLocationFromContextMenu = () => {
    if (!contextMenu) return;

    const { lat, lng } = contextMenu;

    if (marker.current) {
      marker.current.setLngLat([lng, lat]);
    } else {
      marker.current = new mapboxgl.Marker({ draggable: true })
        .setLngLat([lng, lat])
        .addTo(map.current!);

      marker.current.on('dragend', () => {
        const lngLat = marker.current!.getLngLat();
        const dragLat = parseFloat(lngLat.lat.toFixed(6));
        const dragLng = parseFloat(lngLat.lng.toFixed(6));
        setCurrentLocation({ lat: dragLat, lng: dragLng });
        setCurrentLocationAddress(`${dragLat}, ${dragLng}`);
        setSelectedLocationIndex(null);
      });
    }

    setCurrentLocation({ lat, lng });
    setCurrentLocationAddress(`${lat}, ${lng}`);
    setSelectedLocationIndex(null);
    setContextMenu(null); // Close context menu
  };

  // Stable callbacks + memoized objects for the (memoized) ProductsSection
  // and OrderRightSidebar. Keeping these referentially stable lets the two
  // heaviest children skip re-rendering when unrelated fields (PO number,
  // billing, customer) change.
  // Assigning/clearing a stock unit only changes which physical units are on
  // the line — not qty, price, or tax — so we apply the change optimistically
  // and skip the order refetch entirely. The refetch was the source of the
  // lag: it's slow AND Booqable's GET echoes the just-changed assignment for
  // ~1s, which would clobber the optimistic update and flicker the pill back.
  const handleStockItemsChanged = useCallback(
    (change: { lineId: string; add?: OrderLineStockUnit; remove?: string }) => {
      setOrderLines((prev) =>
        prev.map((l) => {
          if (l.id !== change.lineId) return l;
          let units = l.stockUnits || [];
          if (change.remove) units = units.filter((u) => u.id !== change.remove);
          if (change.add && !units.some((u) => u.id === change.add!.id)) {
            units = [...units, change.add];
          }
          return { ...l, stockUnits: units };
        }),
      );
    },
    [],
  );
  const handleOrderSidebarClose = useCallback(() => setDetailsOpen(false), []);

  const handleAddCustomLine = useCallback(async (payload: {
    title: string;
    quantity: number;
    priceEachInCents: number;
    taxCategoryId?: string | null;
    taxExempt?: boolean;
  }) => {
    if (isNewOrder || !orderId) return;
    const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}/lines/custom`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: payload.title,
        quantity: payload.quantity,
        price_each_in_cents: payload.priceEachInCents,
        tax_category_id: payload.taxCategoryId,
        // Only lever Booqable honors for exemption; omit unless exempt so
        // ordinary lines keep their default taxable=true.
        taxable: payload.taxExempt ? false : undefined,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Add custom line failed (${res.status})`);
    }
    setLineRefetchTrigger((c) => c + 1);
    refreshOrderInvoices();
  }, [isNewOrder, orderId, refreshOrderInvoices]);

  const handleAddHeavyAddon = useCallback(async () => {
    if (isNewOrder || !orderId) return;
    const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}/lines/heavy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.error || `Add heavy add-on failed (${res.status})`);
      return;
    }
    setLineRefetchTrigger((c) => c + 1);
    refreshOrderInvoices();
  }, [isNewOrder, orderId, refreshOrderInvoices]);

  const handleRecalculatePrices = useCallback(async () => {
    if (!orderId) return;
    const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}/recalculate-prices`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pickup_date: pickupDate, return_date: returnDate }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.error || `Recalculate failed (${res.status})`);
      return;
    }
    setRecalcBanner(null);
    // Recalc re-stamps the order's tax region to the current customer's, so
    // advance the baseline to match — otherwise the tax banner would re-fire.
    orderTaxRegionRef.current = customerTaxRegionRef.current;
    setLineRefetchTrigger((c) => c + 1);
    refreshOrderInvoices();
  }, [orderId, pickupDate, returnDate, refreshOrderInvoices]);

  const productsRecalcBanner = useMemo(
    () => (recalcBanner && orderId
      ? {
          reason: recalcBanner.reason,
          onDismiss: () => setRecalcBanner(null),
          onRecalculate: handleRecalculatePrices,
        }
      : undefined),
    [recalcBanner, orderId, handleRecalculatePrices],
  );

  const sidebarBillingAddress = useMemo(
    () => ({
      line1: billingAddressLine1,
      line2: billingAddressLine2,
      city: billingCity,
      region: billingRegion,
      postalCode: billingPostalCode,
      country: billingCountry,
    }),
    [billingAddressLine1, billingAddressLine2, billingCity, billingRegion, billingPostalCode, billingCountry],
  );

  // --- In-order phases (sections) ------------------------------------------
  // An order with no explicit sections is a single-window order, rendered flat —
  // this IS the single-phase case (a regular order). "Add phase" turns it into a
  // phased order; once it has sections every line lives in a phase (there is no
  // "unscheduled" bucket), and each section renders as a phase card. The flow
  // never sits at exactly one section: the first Add phase lands two (Phase 1 +
  // its split), and removing the last extra phase dissolves back to a flat order.
  const hasPhases = orderSections.length >= 1;
  const phaseMoveOptions: MovePhaseOption[] = useMemo(
    () =>
      [...orderSections]
        .sort((a, b) => a.sequence - b.sequence)
        .map((s) => ({
          id: s.id,
          label: s.label || `Phase ${s.sequence}`,
          disabled: !!s.activated_at, // billed phases are frozen
        })),
    [orderSections],
  );
  const handleMoveLineToPhase = useCallback(
    async (lineId: string, sectionId: string | null) => {
      if (!orderId) return;
      try {
        await assignLineSection(orderId, lineId, sectionId);
      } catch (e) {
        console.error('Move line to phase failed:', e);
      } finally {
        setLineRefetchTrigger((c) => c + 1);
      }
    },
    [orderId],
  );
  // Stable so OrderSections' umbrella-report effect doesn't re-fire each render.
  // The guard skips redundant updates (same window) to avoid a render loop.
  const handleUmbrellaChange = useCallback((start: string | null, end: string | null) => {
    setPhaseUmbrella((prev) =>
      prev && prev.start === start && prev.end === end ? prev : { start, end },
    );
  }, []);
  // Turn a flat order into a phased one in a single click. Phase 1 is seeded with
  // the order's current window (the backend sweeps every existing line into it, so
  // nothing is left unscheduled), then we immediately split off Phase 2 — so the
  // order lands as a real two-phase order rather than a lone phase that still
  // looks regular. Subsequent phases are added from the OrderSections header.
  const handleAddPhase = useCallback(async () => {
    if (!orderId) return;
    try {
      const toIso = (ymd: string) => (ymd ? `${ymd}T00:00:00.000Z` : undefined);
      await addSection(orderId, { starts_at: toIso(pickupDate), stops_at: toIso(returnDate) });
      await addSection(orderId, {});
    } catch (e) {
      console.error('Add phase failed:', e);
    } finally {
      setLineRefetchTrigger((c) => c + 1);
    }
  }, [orderId, pickupDate, returnDate]);

  // Remove-phase confirm: removing a phase that still holds line items deletes
  // those lines (the user picked delete-on-remove), so it routes through a
  // confirmation. An empty phase removes straight away.
  const [pendingRemovePhase, setPendingRemovePhase] = useState<OrderSection | null>(null);

  // Archive a phase and (per the delete-on-remove choice) delete its line items.
  // Then keep the model consistent: if only one phase would be left, dissolve it
  // too so the order returns to a plain single-window order — its lines detach
  // back to order-level (the backend SET NULLs them on the last delete). One
  // refetch at the end reconciles totals + the collapsed umbrella.
  const performRemovePhase = useCallback(async (section: OrderSection) => {
    if (!orderId) return;
    try {
      const sectionLineIds = orderLines
        .filter((l) => (lineSectionMap[l.id] ?? null) === section.id)
        .map((l) => l.id);
      for (const lineId of sectionLineIds) {
        deletedLineIdsRef.current.add(lineId);
        const res = await apiFetch(`/booqable/lines/${encodeURIComponent(lineId)}`, { method: 'DELETE' });
        if (!res.ok) console.error('[remove-phase] line delete failed:', res.status, await res.text());
      }
      await deleteSection(orderId, section.id);
      // Dissolve back to a flat order when a single phase would remain — the
      // surviving phase's lines become plain order lines again.
      const remaining = orderSections.filter((s) => s.id !== section.id);
      if (remaining.length === 1) {
        await deleteSection(orderId, remaining[0].id);
      }
    } catch (e) {
      console.error('Remove phase failed:', e);
    } finally {
      setLineRefetchTrigger((c) => c + 1);
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
    }
  }, [orderId, orderLines, lineSectionMap, orderSections, queryClient]);

  // Phase remove entry point handed to OrderSections. Empty phase → remove now;
  // phase with lines → confirm (the lines will be deleted).
  const handleRequestRemovePhase = useCallback((section: OrderSection) => {
    const hasLines = orderLines.some((l) => (lineSectionMap[l.id] ?? null) === section.id);
    if (hasLines) {
      setPendingRemovePhase(section);
    } else {
      void performRemovePhase(section);
    }
  }, [orderLines, lineSectionMap, performRemovePhase]);

  if (loadingOrder) {
    return <OrderDetailsSkeleton />;
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '16px' }}>
        <Text size={500} weight="semibold">Error loading order</Text>
        <Text>{error}</Text>
      </div>
    );
  }

  // Title for the inline Header. New orders read as "New order"; existing
  // orders show the Booqable order number once it's been fetched, falling
  // back to a generic "Order" while loading.
  const headerTitle = isNewOrder
    ? 'New order'
    : orderNumber
      ? `#${orderNumber}`
      : 'Order';

  // Top delivery/pickup display. When phased, it's the read-only umbrella = the
  // live phase span OrderSections reports (instant + optimistic). Truly elastic:
  // an interior hinge edit leaves the outer bounds (umbrella holds); resizing the
  // first/last edge moves it. Falls back to the order window until phases report.
  const topPickupDate = hasPhases ? (phaseUmbrella?.start || pickupDate) : pickupDate;
  const topReturnDate = hasPhases ? (phaseUmbrella?.end || returnDate) : returnDate;

  // The flat products list for a single-window (regular) order. Only rendered
  // when the order has no phases — a phased order's lines live in phase cards,
  // not here, so there are no bucket/move-to-phase affordances to add.
  const productsEl = (
    <ProductsSection
      callForPrice={callForPrice}
      tier={tier}
      municipalityName={municipalityName}
      serviceAddressMissing={serviceAddressMissing}
      serviceAddressCaptured={serviceAddressCaptured}
      pricingResolving={municipalityResolving || tierResolving}
      pricingError={pricingError}
      isAddingLine={isAddingLine}
      pickupDate={pickupDate}
      returnDate={returnDate}
      isMonthly={isMonthly}
      onProductSelect={handleProductSelect}
      lines={orderLines}
      title="Products"
      subtotalInCents={displaySubtotalCents}
      taxInCents={displayTaxCents}
      taxLabel={orderTaxLabel}
      totalInCents={displayTotalCents}
      prWithholdingInCents={orderWithholdingCents}
      prWithholdingRate={orderWithholdingLabel}
      withholdingAmountCents={withholdingAmountCents}
      autoWithholdingCents={autoWithholdingCents}
      autoWithholdingRate={autoWithholdingLabel}
      onSetWithholdingAmount={!isNewOrder && orderId ? handleSetWithholdingAmount : undefined}
      onUpdateLineQuantity={handleUpdateLineQuantity}
      onUpdateLinePrice={handleUpdateLinePrice}
      onUpdateLineExtraInfo={!isNewOrder && orderId ? handleUpdateLineExtraInfo : undefined}
      onRemoveLine={handleRemoveLine}
      onStockItemsChanged={handleStockItemsChanged}
      orderIdForStockPicker={isNewOrder ? null : orderId}
      orderLocalId={isNewOrder ? null : orderId}
      onTonnageLinesChanged={handleTonnageLinesChanged}
      customerName={selectedCustomer?.name ?? null}
      showIcon={false}
      recalcBanner={productsRecalcBanner}
      shortageNotice={
        shortageNotice
          ? { message: shortageNotice.message, onDismiss: () => setShortageNotice(null) }
          : null
      }
      onAddCustomLine={!isNewOrder && orderId ? handleAddCustomLine : undefined}
      onAddHeavyAddon={!isNewOrder && orderId ? handleAddHeavyAddon : undefined}
      readOnly={editLocked}
    />
  );

  // Order-level charges, shown below the phases on a phased order. Product lines
  // live in phase cards; this hosts the order-wide custom-line control and lists
  // any order-level (non-phase) lines. Picker hidden + totals deferred to the
  // sidebar (chargesOnly + hideTotals). lines = the null-section (order-level)
  // lines. Only rendered when phased.
  const orderChargesLines = orderLines.filter((l) => !lineSectionMap[l.id]);
  const orderChargesEl = (
    <ProductsSection
      callForPrice={callForPrice}
      tier={tier}
      municipalityName={municipalityName}
      serviceAddressMissing={serviceAddressMissing}
      serviceAddressCaptured={serviceAddressCaptured}
      pricingResolving={municipalityResolving || tierResolving}
      pricingError={pricingError}
      isAddingLine={isAddingLine}
      pickupDate={pickupDate}
      returnDate={returnDate}
      onProductSelect={handleProductSelect}
      lines={orderChargesLines}
      title="Order charges"
      chargesOnly
      hideTotals
      taxLabel={orderTaxLabel}
      // Withholding is an order-level figure, so a phased order's manual
      // adjustment lives here (the order-charges block), not in the phase cards.
      // totalInCents feeds the dialog's Balance-due preview (no on-screen totals
      // render here — hideTotals).
      totalInCents={displayTotalCents}
      withholdingAmountCents={withholdingAmountCents}
      autoWithholdingCents={autoWithholdingCents}
      autoWithholdingRate={autoWithholdingLabel}
      onSetWithholdingAmount={!isNewOrder && orderId ? handleSetWithholdingAmount : undefined}
      onUpdateLineQuantity={handleUpdateLineQuantity}
      onUpdateLinePrice={handleUpdateLinePrice}
      onUpdateLineExtraInfo={!isNewOrder && orderId ? handleUpdateLineExtraInfo : undefined}
      onRemoveLine={handleRemoveLine}
      onStockItemsChanged={handleStockItemsChanged}
      orderIdForStockPicker={isNewOrder ? null : orderId}
      orderLocalId={isNewOrder ? null : orderId}
      onTonnageLinesChanged={handleTonnageLinesChanged}
      customerName={selectedCustomer?.name ?? null}
      showIcon={false}
      onAddCustomLine={!isNewOrder && orderId ? handleAddCustomLine : undefined}
      readOnly={editLocked}
    />
  );

  // A phase card's body: the same rich ProductsSection scoped to one section's
  // lines + window, with its own section-aware picker (PhaseProducts owns the
  // hook). Compact hides the order-level chrome; lines are draggable + carry the
  // Move-to-phase menu. Custom-line/discount/coupon stay order-level (Order charges).
  const renderPhaseProducts = (section: OrderSection) => {
    const sectionLines = orderLines.filter((l) => (lineSectionMap[l.id] ?? null) === section.id);
    const ymd = (iso: string | null) => (iso ? iso.slice(0, 10) : '');
    return (
      <PhaseProducts
        orderId={orderId!}
        sectionId={section.id}
        // Same optimistic add lifecycle as every other add path — the
        // placeholder lands in this card (tagged with section.id), then
        // handleLineAdded settles it and runs reconcile + refetch in the
        // background. No separate phase reconcile.
        onLineAddStart={handleLineAddStart}
        onLineAdded={handleLineAdded}
        onLineAddFailed={handleLineAddFailed}
        compact
        draggableLines
        hideTotals
        lines={sectionLines}
        pickupDate={ymd(section.starts_at)}
        returnDate={ymd(section.stops_at)}
        callForPrice={callForPrice}
        tier={tier}
        municipalityName={municipalityName}
        serviceAddressMissing={serviceAddressMissing}
        serviceAddressCaptured={serviceAddressCaptured}
        pricingResolving={municipalityResolving || tierResolving}
        pricingError={pricingError}
        isAddingLine={isAddingLine}
        phaseOptions={phaseMoveOptions}
        lineSectionId={(id) => lineSectionMap[id] ?? null}
        onMoveLineToPhase={handleMoveLineToPhase}
        onUpdateLineQuantity={handleUpdateLineQuantity}
        onUpdateLinePrice={handleUpdateLinePrice}
        onUpdateLineExtraInfo={!isNewOrder && orderId ? handleUpdateLineExtraInfo : undefined}
        onRemoveLine={handleRemoveLine}
        onStockItemsChanged={handleStockItemsChanged}
        orderIdForStockPicker={orderId}
        orderLocalId={orderId}
        onTonnageLinesChanged={handleTonnageLinesChanged}
        customerName={selectedCustomer?.name ?? null}
        showIcon={false}
        readOnly={editLocked}
      />
    );
  };

  return (
    <>
      {/* The workspace card MUST be .main-content's first child. The app-shell
          gutter rule (app.css `.main-content > :first-child`) gives the first
          child `margin: 8px 8px 0` (no bottom margin) — and that is exactly
          what the card's `height: calc(100vh - 64px)` is sized against, so it
          fills the shell precisely without spilling into a second, outer
          scrollbar (which dropped the right sidebar out of view on scroll).
          The Toaster + create-customer drawer therefore render AFTER the card;
          both portal / position:fixed, so their DOM order is immaterial. */}
      <div className={styles.workspaceCard}>
        <div className={headerScrolled ? `${styles.headerWrap} ${styles.headerWrapScrolled}` : styles.headerWrap}>
        <Header
          inline
          stackTitleOnMobile
          customActions={
            <div className={styles.actionCluster}>
            {/* Payment status sits on the action level (its own row on
                mobile) next to the lifecycle button, not in the title. */}
            {!isNewOrder && paymentStatus && (
              <Tooltip content="Payment status" relationship="label">
                <span className={styles.statusTipTarget}>
                  <PaymentStatusPill status={paymentStatus} icon={<Money16Regular />} />
                </span>
              </Tooltip>
            )}
            {(() => {
            // Before promotion there's no Booqable order to transition. The
            // draft auto-creates once a customer is selected and promotes once
            // it has minimal info (customer + dates + location) — so surface
            // status instead of a button: a hint until the customer is set,
            // then the autosave/creation pill.
            if (!isPromoted) {
              if (!selectedCustomer) {
                return (
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    Select a customer to start the order
                  </Text>
                );
              }
              const status = isNewOrder || savingDraft
                ? (draftCreateError ? 'error' : 'saving')
                : saveStatus;
              // Only new orders show the autosave pill here — once the order
              // is saved (non-new) the title row renders one next to the
              // status pill, so showing it here too would duplicate it for a
              // saved-but-not-yet-promoted order.
              return isNewOrder ? <SaveStatusBadge status={status} /> : null;
            }
            // Status-driven transition button. One primary button per state
            // for the forward lifecycle (draft → reserved → started → stopped
            // → archived). Secondary actions surface contextually: a ⋯ menu
            // with Cancel on draft/reserved (cancel is a side exit, valid only
            // from those states), a Revert menu on stopped (back to
            // started/reserved/draft), and an Un-archive action on archived.
            const hasRentalPeriod = !!pickupDate && !!returnDate;
            const hasLines = orderLines.length > 0;

            // ⋯ overflow with the secondary actions for draft/reserved. Kept
            // out of the way since these are less common than the forward
            // action. Reserved can also step back to draft (un-reserve);
            // both states can cancel (destructive, styled red).
            const overflowMenu = (
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <Button
                    appearance="subtle"
                    icon={<MoreHorizontal20Regular />}
                    aria-label="More order actions"
                    disabled={isTransitioning || isReverting}
                  />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    {orderStatus === 'reserved' && (
                      <MenuItem
                        icon={<Mail20Regular />}
                        onClick={handleSendConfirmation}
                        disabled={isSendingConfirmation}
                      >
                        {isSendingConfirmation ? 'Sending…' : 'Send confirmation email'}
                      </MenuItem>
                    )}
                    {orderStatus === 'reserved' && (
                      <MenuItem
                        icon={<ArrowUndo20Regular />}
                        onClick={() => setRevertTarget('draft')}
                      >
                        Revert to draft
                      </MenuItem>
                    )}
                    <MenuItem
                      icon={<DismissCircle20Regular />}
                      onClick={() => setCancelDialogOpen(true)}
                      style={{ color: tokens.colorPaletteRedForeground1 }}
                    >
                      Cancel order
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
            );

            if (orderStatus === 'draft' && hasRentalPeriod) {
              return (
                <>
                  <Button
                    appearance="primary"
                    icon={<LockClosed20Regular />}
                    onClick={handleReserve}
                    disabled={isReserving || missingProhibitedAck}
                  >
                    {isReserving ? 'Reserving…' : 'Reserve'}
                  </Button>
                  {missingProhibitedAck && (
                    <Tooltip
                      content="Confirm the prohibited-items list with the customer and check the acknowledgment (in Customer notes) before reserving."
                      relationship="label"
                    >
                      <Info16Regular tabIndex={0} style={{ color: tokens.colorPaletteRedForeground1 }} />
                    </Tooltip>
                  )}
                  {overflowMenu}
                </>
              );
            }
            if (orderStatus === 'reserved' && hasLines) {
              return (
                <>
                  <Button
                    appearance="primary"
                    onClick={handleStart}
                    disabled={isTransitioning || isReverting}
                  >
                    {isTransitioning ? 'Starting…' : 'Start'}
                  </Button>
                  {overflowMenu}
                </>
              );
            }
            if (orderStatus === 'started') {
              // Stop stays the primary forward action; the ⋯ menu reverts
              // straight back to reserved or draft (single revert call each —
              // see /revert's ALLOWED_REVERTS). Ordered draft→reserved to match
              // the stopped-order menu below.
              return (
                <>
                  <Button
                    appearance="primary"
                    onClick={handleStop}
                    disabled={isTransitioning || isReverting}
                  >
                    {isTransitioning ? 'Completing…' : 'Complete'}
                  </Button>
                  <Menu>
                    <MenuTrigger disableButtonEnhancement>
                      <Button
                        appearance="subtle"
                        icon={<MoreHorizontal20Regular />}
                        aria-label="More order actions"
                        disabled={isTransitioning || isReverting}
                      />
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem
                          icon={<ArrowUndo20Regular />}
                          onClick={() => setRevertTarget('draft')}
                        >
                          Revert to draft
                        </MenuItem>
                        <MenuItem
                          icon={<ArrowUndo20Regular />}
                          onClick={() => setRevertTarget('reserved')}
                        >
                          Revert to reserved
                        </MenuItem>
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </>
              );
            }
            if (orderStatus === 'stopped') {
              // Archive stays the primary forward action; the ⋯ menu mirrors
              // Booqable's native stopped-order menu, reverting straight to an
              // earlier status (each is a single revert call — see /revert's
              // ALLOWED_REVERTS). Ordered draft→reserved→started to match
              // Booqable's own menu.
              return (
                <>
                  <Button
                    appearance="primary"
                    icon={<Archive20Regular />}
                    onClick={handleArchive}
                    disabled={isTransitioning || isReverting}
                  >
                    {isTransitioning ? 'Archiving…' : 'Archive'}
                  </Button>
                  <Menu>
                    <MenuTrigger disableButtonEnhancement>
                      <Button
                        appearance="subtle"
                        icon={<MoreHorizontal20Regular />}
                        aria-label="More order actions"
                        disabled={isTransitioning || isReverting}
                      />
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem
                          icon={<ArrowUndo20Regular />}
                          onClick={() => setRevertTarget('draft')}
                        >
                          Revert to draft
                        </MenuItem>
                        <MenuItem
                          icon={<ArrowUndo20Regular />}
                          onClick={() => setRevertTarget('reserved')}
                        >
                          Revert to reserved
                        </MenuItem>
                        <MenuItem
                          icon={<ArrowUndo20Regular />}
                          onClick={() => setRevertTarget('started')}
                        >
                          Revert to started
                        </MenuItem>
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </>
              );
            }
            if (orderStatus === 'archived') {
              // An order only reaches `archived` from `stopped` (Archive is
              // offered solely on stopped), so the single legal un-archive
              // target is stopped. The old 3-way menu also offered reserved/
              // started — illegal multi-step reverts that desynced state (#614).
              return (
                <Button
                  appearance="primary"
                  icon={<ArrowUndo20Regular />}
                  onClick={() => setRevertTarget('stopped')}
                  disabled={isReverting}
                >
                  {isReverting ? 'Un-archiving…' : 'Un-archive'}
                </Button>
              );
            }
            return undefined;
            })()}
            {/* Mobile-only toggle for the right details sidebar, which renders
                as a slide-in overlay on phones. Sits at the far right of the
                action cluster. Hidden on desktop where the sidebar is inline. */}
            {isMobile && (
              <Tooltip content={detailsOpen ? 'Hide details' : 'Show details'} relationship="label">
                <Button
                  appearance={detailsOpen ? 'primary' : 'subtle'}
                  icon={<PanelRight24Regular />}
                  // Blur on tap so Fluent's leave handler cancels the pending
                  // 250ms show timer — otherwise the tooltip pops up over the
                  // just-opened sidebar and lingers (touch keeps focus, so it
                  // never auto-hides).
                  onClick={(e) => {
                    setDetailsOpen((v) => !v);
                    e.currentTarget.blur();
                  }}
                  aria-label={detailsOpen ? 'Hide details' : 'Show details'}
                />
              </Tooltip>
            )}
            </div>
          }
          customTitle={
            <div className={styles.headerTitleRow}>
              <div className={styles.headerTitleLeft}>
                {/* Breadcrumb: Orders > {headerTitle}. Mirrors the
                    customer-profile breadcrumb so /orders and /orders/:id
                    share visual identity. */}
                <Link
                  onClick={(e) => { e.preventDefault(); navigate('/orders'); }}
                  href="/orders"
                  style={{
                    fontSize: tokens.fontSizeBase500,
                    fontWeight: tokens.fontWeightSemibold,
                    lineHeight: tokens.lineHeightBase300,
                    color: tokens.colorNeutralForeground1,
                  }}
                >
                  Orders
                </Link>
                <ChevronRight16Regular style={{ color: tokens.colorNeutralForeground3 }} />
                <span style={{
                  fontSize: tokens.fontSizeBase500,
                  fontWeight: tokens.fontWeightSemibold,
                  lineHeight: tokens.lineHeightBase300,
                  color: tokens.colorNeutralForeground1,
                }}>
                  {headerTitle}
                </span>
                {!isNewOrder && orderStatus && (
                  <Tooltip content="Order status" relationship="label">
                    <span className={styles.statusTipTarget}>
                      <OrderStatusPill status={orderStatus} />
                    </span>
                  </Tooltip>
                )}
                {!isNewOrder && <SaveStatusBadge status={saveStatus} />}
              </div>
            </div>
          }
        />
        </div>
        <div className={styles.body}>
          <div
            className={styles.mainColumn}
            onScroll={(e) => {
              const s = e.currentTarget.scrollTop > 0;
              setHeaderScrolled((prev) => (prev === s ? prev : s));
            }}
          >
            {/* Two-column top row: Customer + Location stacked on the left,
                Billing & rental period on the right. Products span full
                width below as a single column. */}
            <div className={styles.workArea}>
              {editLocked && (
                <div className={styles.lockBanner}>
                  <LockClosed20Regular
                    style={{ color: tokens.colorPalettePumpkinBorderActive, flexShrink: 0 }}
                  />
                  <Text className={styles.lockBannerText}>
                    This order is {orderStatus === 'started' ? 'in dispatch' : 'completed'}. Fields
                    are locked to prevent accidental changes.
                  </Text>
                  <Button
                    appearance="primary"
                    size="medium"
                    icon={<Edit20Regular />}
                    onClick={() => setEditUnlocked(true)}
                  >
                    Edit order
                  </Button>
                </div>
              )}
              {/* Unlocked counterpart of the guard banner: while editing a
                  dispatched/completed order, offer an explicit way back to the
                  protected view-only state without a status change. */}
              {!isNewOrder && completedLike && editUnlocked && (
                <div className={styles.lockBanner}>
                  <Edit20Regular
                    style={{ color: tokens.colorPalettePumpkinBorderActive, flexShrink: 0 }}
                  />
                  <Text className={styles.lockBannerText}>
                    Editing a {orderStatus === 'started' ? 'dispatched' : 'completed'} order. Fields
                    are unlocked.
                  </Text>
                  <Button
                    appearance="secondary"
                    size="medium"
                    icon={<LockClosed20Regular />}
                    onClick={() => setEditUnlocked(false)}
                  >
                    Done editing
                  </Button>
                </div>
              )}
              <div className={styles.topRow}>
                <div className={styles.topLeftColumn}>
                  <CustomerSection
                    selectedCustomer={selectedCustomer}
                    onCustomerSelect={setSelectedCustomer}
                    onCustomerClear={handleCustomerClear}
                    onAddCustomer={(phaseLocked || editLocked) ? undefined : () => setAddCustomerOpen(true)}
                    showIcon={false}
                    readOnly={phaseLocked || editLocked}
                    manageLabel={phaseLocked ? `Managed on project P-${orderProject?.projectNumber}` : undefined}
                    onManageClick={phaseLocked ? () => navigate(`/projects/${orderProject?.id}`) : undefined}
                  />
                  <LocationSection
                    customerLocations={customerLocations}
                    selectedLocationIndex={selectedLocationIndex}
                    onLocationIndexSelect={handleLocationSelect}
                    currentLocation={phaseLocked ? phaseLocation : currentLocation}
                    currentLocationAddress={phaseLocked ? (orderProject?.deliveryAddress || '') : currentLocationAddress}
                    currentLocationName={phaseLocked ? (orderProject?.deliveryLocationName || '') : currentLocationName}
                    onLocationPickerSelect={handleLocationPickerSelect}
                    hasCustomerSelected={selectedCustomer !== null}
                    onSaveLocationToCustomer={handleSaveLocationToCustomerClick}
                    onAddLocationToOrder={handleAddLocationToOrder}
                    onRemoveLocation={(phaseLocked || editLocked) ? undefined : handleRemoveLocation}
                    showIcon={false}
                    readOnly={phaseLocked || editLocked}
                    manageLabel={phaseLocked ? `Managed on project P-${orderProject?.projectNumber}` : undefined}
                    onManageClick={phaseLocked ? () => navigate(`/projects/${orderProject?.id}`) : undefined}
                  />
                  <OrderCustomerNotesSection orderLocalId={isNewOrder ? null : orderId} locked={editLocked} />
                </div>
                <div className={styles.topRightColumn}>
                  <BillingRentalSection
                    savedBillingAddress={savedBillingAddress}
                    onAddBillingAddress={handleAddBillingAddress}
                    onChangeBillingAddress={handleChangeBillingAddress}
                    onRemoveBillingAddress={handleRemoveBillingAddress}
                    pickupDate={phaseLocked ? (phaseEntry?.startDate || '') : topPickupDate}
                    returnDate={phaseLocked ? (phaseEntry?.endDate || '') : topReturnDate}
                    onPickupDateChange={phaseLocked ? ((ymd) => onPhaseDate('start', ymd)) : handlePickupDateChange}
                    onReturnDateChange={phaseLocked ? ((ymd) => onPhaseDate('end', ymd)) : handleReturnDateChange}
                    monthly={!phaseLocked && isMonthly}
                    onMonthlyChange={phaseLocked || editLocked ? undefined : setIsMonthly}
                    poNumber={phaseLocked ? (orderProject?.poNumber || '') : orderPoNumber}
                    onPoNumberChange={phaseLocked ? undefined : setOrderPoNumber}
                    poRequired={!!selectedCustomer?.poRequired}
                    poReadOnly={phaseLocked}
                    poNote={phaseLocked ? 'Inherited from the project' : undefined}
                    paymentTerms={orderPaymentTerms}
                    onPaymentTermsChange={editLocked ? undefined : setOrderPaymentTerms}
                    projectName={orderProjectName}
                    onProjectNameChange={editLocked ? undefined : setOrderProjectName}
                    showIcon={false}
                    readOnly={editLocked}
                    phaseMode={phaseLocked}
                    // Multi-phase umbrella: the top dates span the phases below,
                    // so label them as the project window (Start/Completion).
                    projectWindow={hasPhases}
                    // Multi-phase: the order window is the computed umbrella (rollup
                    // of the phases), so the top pickers are read-only here — dates
                    // are edited per phase below.
                    pickupDisabled={hasPhases || (phaseLocked && phasePickupDisabled)}
                    returnDisabled={hasPhases || (phaseLocked && phaseReturnDisabled)}
                    deliveryMinDate={phaseLocked ? phaseDeliveryMin : undefined}
                    deliveryMaxDate={phaseLocked ? phaseDeliveryMax : undefined}
                    pickupMinDate={phaseLocked ? phasePickupMin : undefined}
                    pickupMaxDate={phaseLocked ? phasePickupMax : undefined}
                    datesNote={
                      phaseLocked
                        ? phaseDatesNote
                        : hasPhases
                          ? 'Spans the phases below — edit delivery & pickup on each phase.'
                          : undefined
                    }
                    datesFooter={
                      phaseLocked ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '12px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <PhaseCascadeChoice
                            ripple={phaseShiftLater}
                            onChange={setPhaseShiftLater}
                            layout="horizontal"
                          />
                          {phaseScheduleDirty && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {phaseSaveError && (
                                <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>
                                  {phaseSaveError}
                                </Text>
                              )}
                              <Button appearance="subtle" onClick={handleDiscardPhaseSchedule} disabled={phaseSaving}>
                                Discard
                              </Button>
                              <Button appearance="primary" onClick={handleSavePhaseSchedule} disabled={phaseSaving}>
                                {phaseSaving ? 'Saving…' : 'Save schedule'}
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : undefined
                    }
                  />
                  {/* #1178 soft guard: this customer requires a PO but the
                      order's PO field (above) is empty. Non-blocking — the hard
                      block is enforced at invoice finalize on the backend. */}
                  {selectedCustomer?.poRequired && !orderPoNumber.trim() && !phaseLocked && (
                    <div className={styles.poWarnBanner}>
                      <Warning20Regular
                        style={{ color: tokens.colorPalettePumpkinBorderActive, flexShrink: 0 }}
                      />
                      <Text className={styles.lockBannerText}>
                        This customer requires a PO number. Enter it above before finalizing the invoice.
                      </Text>
                    </div>
                  )}
                </div>
              </div>
              {!isPromoted ? (
                // Lines POST to Booqable per add, which requires a real
                // Booqable order. The draft auto-creates on customer-select and
                // promotes immediately (customer-only — dates/location aren't
                // prerequisites), so this placeholder shows only until a
                // customer is picked and the brief promote round-trip lands.
                // Day-2 enhancement: buffer lines locally and batch-POST on
                // promotion (see GH issue).
                <SectionShell title="Products" icon={<Box24Regular />} showIcon={false}>
                  <div style={{
                    textAlign: 'center',
                    padding: '32px 16px',
                    color: tokens.colorNeutralForeground3,
                    fontSize: '14px',
                  }}>
                    {!selectedCustomer
                      ? 'Select a customer to start the order'
                      : 'Starting order…'}
                  </div>
                </SectionShell>
              ) : loadingLines ? (
                // Progressive render: chrome is up; the Booqable include (lines +
                // totals) is still in flight. Show a table skeleton here only so
                // the section keeps its shape while the rows fill in.
                <SectionShell title="Products" icon={<Box24Regular />} showIcon={false}>
                  <ProductsSkeleton />
                </SectionShell>
              ) : hasPhases ? (
                // Phased order: the timeline + phase cards own every line. No
                // unscheduled bucket — a line always belongs to a phase. Order-
                // level charges sit in their own block below the phases.
                <>
                <OrderSections
                  orderId={orderId!}
                  orderStartDate={pickupDate || null}
                  orderEndDate={returnDate || null}
                  sections={orderSections}
                  orderLines={orderLines.map((l) => ({
                    id: l.id,
                    name: l.name,
                    quantity: l.quantity,
                    totalInCents: l.totalInCents,
                  }))}
                  lineSectionMap={lineSectionMap}
                  onChanged={() => setLineRefetchTrigger((c) => c + 1)}
                  renderPhaseProducts={renderPhaseProducts}
                  onRequestRemovePhase={handleRequestRemovePhase}
                  onUmbrellaChange={handleUmbrellaChange}
                />
                {orderChargesEl}
                </>
              ) : (
                // Single-window (regular) order: flat products list + the entry
                // point to phase it. "Add phase" lands the first two phases.
                <>
                  {productsEl}
                  {!isNewOrder && orderId && !editLocked && (
                    <div style={{ marginTop: '12px' }}>
                      <Button
                        appearance="subtle"
                        icon={<CalendarClock20Regular />}
                        onClick={handleAddPhase}
                      >
                        Add phase
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <OrderRightSidebar
            orderId={isPromoted ? orderId : null}
            orderLocalId={isNewOrder ? null : orderId}
            isPromoted={isPromoted}
            orderNumber={orderNumber}
            customerId={selectedCustomer?.id}
            customerEmail={selectedCustomer?.email}
            customerName={selectedCustomer?.name}
            customerPhone={selectedCustomer?.phone}
            billingAddress={sidebarBillingAddress}
            orderLineItems={orderLines}
            orderTaxCents={orderTaxCents}
            paymentStatus={isPromoted ? paymentStatus : null}
            collapsed={isMobile ? !detailsOpen : false}
            onClose={handleOrderSidebarClose}
            isPhase={isPhase}
            allowProjectOverride={allowProjectOverride}
            onAllowProjectOverrideChange={setAllowProjectOverride}
          />
        </div>
      </div>

      <Toaster toasterId={toasterId} />
      {/* Create-customer drawer opened from the Customer section's Add icon.
          showKindSelector renders the kind tabs at the top (this surface has
          no Customers-page Add SplitButton). allowedKinds drops the Contact
          tab because a Contact can't own an order — mirrors the Add-Order
          action's contact guard (toolbars.tsx) so the inline-create path
          can't circumvent the rule. On save we attach the new record. */}
      <CustomerDrawer
        isOpen={addCustomerOpen}
        showKindSelector
        allowedKinds={['individual', 'company']}
        onClose={() => setAddCustomerOpen(false)}
        onSaved={(customerData) => {
          queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
          const kind = (customerData as any)?.customerKind;
          const attrs = (customerData as any)?.attributes || {};
          const newId = (customerData as any)?.id;
          // Defense in depth: the Contact tab is hidden above, but never
          // attach a Contact to an order even if one somehow reaches here —
          // Contacts belong to a Company and can't own orders.
          if (kind === 'contact') {
            dispatchToast(
              <Toast>
                <ToastTitle>Contact created — not attached</ToastTitle>
                <ToastBody>
                  A Contact can't own an order. Select its parent Company instead.
                </ToastBody>
              </Toast>,
              { intent: 'warning' }
            );
            return;
          }
          if (newId) {
            // Attach the just-created customer to the order. The Booqable
            // response id is the Booqable id, which is what customer-field
            // enrichment + autosave round-trips expect.
            setSelectedCustomer({
              id: newId,
              name: attrs.name || '',
              email: attrs.email || '',
            });
          }
          const entityLabel = kind === 'company' ? 'Company' : 'Customer';
          dispatchToast(
            <Toast>
              <ToastTitle>{`${entityLabel} created`}</ToastTitle>
              <ToastBody>{`${entityLabel} added and attached to this order.`}</ToastBody>
            </Toast>,
            { intent: 'success' }
          );
        }}
      />

      {/* Context Menu for Map */}
      {contextMenu && (
        <>
          {/* Backdrop to close menu on click */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
            onClick={() => setContextMenu(null)}
          />
          {/* Context Menu */}
          <div
            className={styles.contextMenu}
            style={{
              top: `${contextMenu.y}px`,
              left: `${contextMenu.x}px`,
            }}
          >
            <div
              className={styles.contextMenuItem}
              onClick={handleSetLocationFromContextMenu}
            >
              <span>📍</span>
              <span>Set location here</span>
            </div>
          </div>
        </>
      )}

      {/* Add Custom Field Dialog */}
      <Dialog open={addFieldDialogOpen} onOpenChange={(_, data) => setAddFieldDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>New custom field</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <Label htmlFor="customFieldLabel">Custom field label</Label>
                  <Input
                    id="customFieldLabel"
                    size="large"
                    value={customFieldLabel}
                    onChange={(e, data) => setCustomFieldLabel(data.value)}
                    style={{ width: '100%' }}
                  />
                </div>

                <div>
                  <Label htmlFor="dataType">Data type</Label>
                  <Dropdown
                    id="dataType"
                    size="large"
                    value={customFieldDataType}
                    onOptionSelect={(_, data) => setCustomFieldDataType(data.optionText || 'Single line text')}
                    style={{ width: '100%' }}
                  >
                    <Option>Single line text</Option>
                    <Option>Multi line text</Option>
                    <Option>Number</Option>
                    <Option>Date</Option>
                  </Dropdown>
                </div>

                <div>
                  <Label htmlFor="value">Value</Label>
                  <Input
                    id="value"
                    size="large"
                    value={customFieldValue}
                    onChange={(e, data) => setCustomFieldValue(data.value)}
                    style={{ width: '100%' }}
                  />
                </div>

                <div>
                  <Label>Show on</Label>
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', marginTop: '8px' }}>
                    <Checkbox
                      label="Quotes"
                      checked={showOnQuotes}
                      onChange={(e, data) => setShowOnQuotes(data.checked === true)}
                    />
                    <Checkbox
                      label="Contracts"
                      checked={showOnContracts}
                      onChange={(e, data) => setShowOnContracts(data.checked === true)}
                    />
                    <Checkbox
                      label="Invoices"
                      checked={showOnInvoices}
                      onChange={(e, data) => setShowOnInvoices(data.checked === true)}
                    />
                    <Checkbox
                      label="Packing slip"
                      checked={showOnPackingSlip}
                      onChange={(e, data) => setShowOnPackingSlip(data.checked === true)}
                    />
                  </div>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setAddFieldDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={() => {
                setAddFieldDialogOpen(false);
              }}>
                Add field
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Rental-period change confirmation (paid orders only). A date edit here
          does not re-bill the customer, so we make that consequence explicit
          before committing rather than letting staff change it silently. */}
      <Dialog
        open={pendingPeriodChange !== null}
        onOpenChange={(_, data) => { if (!data.open) setPendingPeriodChange(null); }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Change rental period on a paid order?</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <span>
                  This order's invoice is already paid. Changing the{' '}
                  {pendingPeriodChange?.field === 'pickup' ? 'pickup' : 'return'} date{' '}
                  <strong>won't</strong> re-bill the customer or change the paid invoice.
                </span>
                <span>
                  To charge for an extension, add an <strong>Extra Day</strong> line
                  ($75/day) instead. Shortening the period may require a credit or refund.
                  Update the date here only for scheduling and availability.
                </span>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setPendingPeriodChange(null)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={confirmPeriodChange}>
                Update date anyway
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Billing Address Dialog */}
      <Dialog open={billingAddressDialogOpen} onOpenChange={(_, data) => setBillingAddressDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Billing address</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {customerMainAddress && (
                  <div>
                    <Label htmlFor="savedAddress" weight="semibold">Saved address</Label>
                    <Dropdown
                      id="savedAddress"
                      size="large"
                      className={styles.savedAddressDropdown}
                      value={billingMode === 'customer' ? customerMainAddress.display : 'New address'}
                      selectedOptions={[billingMode]}
                      onOptionSelect={(_, data) => {
                        const next = (data.optionValue as 'customer' | 'new') || 'new';
                        setBillingMode(next);
                        if (next === 'customer') {
                          const f = customerMainAddress.fields;
                          setBillingAddressLine1(f.address1);
                          setBillingAddressLine2(f.address2);
                          setBillingCity(f.city);
                          setBillingRegion(f.region);
                          setBillingPostalCode(f.zipcode);
                          setBillingCountry(f.country);
                        }
                      }}
                      style={{ width: '100%' }}
                    >
                      <Option value="new">New address</Option>
                      <Option value="customer">{customerMainAddress.display}</Option>
                    </Dropdown>
                  </div>
                )}

                <AddressFields
                  addressLine1={billingAddressLine1}
                  onAddressLine1Change={(v) => { setBillingAddressLine1(v); if (billingMode === 'customer') setBillingMode('new'); }}
                  addressLine2={billingAddressLine2}
                  onAddressLine2Change={(v) => { setBillingAddressLine2(v); if (billingMode === 'customer') setBillingMode('new'); }}
                  city={billingCity}
                  onCityChange={(v) => { setBillingCity(v); if (billingMode === 'customer') setBillingMode('new'); }}
                  region={billingRegion}
                  onRegionChange={(v) => { setBillingRegion(v); if (billingMode === 'customer') setBillingMode('new'); }}
                  postalCode={billingPostalCode}
                  onPostalCodeChange={(v) => { setBillingPostalCode(v); if (billingMode === 'customer') setBillingMode('new'); }}
                  country={billingCountry}
                  onCountryChange={(v) => { setBillingCountry(v); if (billingMode === 'customer') setBillingMode('new'); }}
                  idPrefix="billing"
                  required
                />
              </div>
            </DialogContent>
            {billingSaveError && (
              <div style={{
                margin: '0 24px',
                padding: '8px 12px',
                backgroundColor: tokens.colorPaletteRedBackground1,
                color: tokens.colorPaletteRedForeground1,
                borderRadius: '4px',
                fontSize: '13px',
              }}>
                {billingSaveError}
              </div>
            )}
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => {
                  setBillingSaveError(null);
                  setBillingAddressDialogOpen(false);
                }}
                disabled={savingBillingAddress}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleSaveBillingAddress}
                disabled={
                  savingBillingAddress ||
                  // Block save until the user has filled the minimum set of
                  // fields a real billing address needs. Address line 2 is
                  // optional; the rest are required.
                  !billingCountry.trim() ||
                  !billingAddressLine1.trim() ||
                  !billingCity.trim() ||
                  !billingRegion.trim() ||
                  !billingPostalCode.trim()
                }
                icon={savingBillingAddress ? <Spinner size="tiny" /> : undefined}
              >
                {savingBillingAddress ? 'Saving…' : 'Save'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Remove Customer Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={removeCustomerDialogOpen}
        onClose={() => setRemoveCustomerDialogOpen(false)}
        onConfirm={performCustomerClear}
        title="Remove customer"
        message={`Remove ${selectedCustomer?.name || 'this customer'} from this order?`}
        confirmButtonText="Remove"
        variant="warning"
      />

      {/* Remove-phase confirmation — only shown when the phase still has line
          items, which will be deleted along with it. */}
      <ConfirmationDialog
        isOpen={pendingRemovePhase !== null}
        onClose={() => setPendingRemovePhase(null)}
        onConfirm={() => {
          const section = pendingRemovePhase;
          setPendingRemovePhase(null);
          if (section) void performRemovePhase(section);
        }}
        title="Remove phase"
        message={`Remove "${pendingRemovePhase?.label || 'this phase'}" and delete its line items?`}
        subtext="The phase's line items will be deleted. This can't be undone."
        confirmButtonText="Remove phase"
        cancelButtonText="Keep phase"
        variant="danger"
      />

      {/* Cancel Order Confirmation Dialog (draft/reserved only) */}
      <ConfirmationDialog
        isOpen={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={performCancel}
        title="Cancel order"
        message={`Cancel order ${orderNumber || ''}?`.trim()}
        subtext="The order will be moved to the canceled state. This can't be undone from here."
        confirmButtonText="Cancel order"
        cancelButtonText="Keep order"
        variant="danger"
      />

      {/* Start: pick which items are going out (per-line; partial start ok) */}
      <StartItemsDialog
        open={startDialogOpen}
        lines={orderLines}
        busy={isTransitioning}
        onCancel={() => setStartDialogOpen(false)}
        onConfirm={({ selectedLineIds, isAllRemaining }) => confirmStart(selectedLineIds, isAllRemaining)}
      />

      {/* Revert (un-archive) Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={revertTarget !== null}
        onClose={() => setRevertTarget(null)}
        onConfirm={() => { if (revertTarget) performRevert(revertTarget); }}
        title="Revert order"
        message={`Revert this order back to ${revertTarget || ''}?`}
        subtext="The order will be returned to the selected status."
        confirmButtonText="Revert"
        variant="warning"
      />

      {/* Save Location to Customer Dialog */}
      <Dialog open={saveLocationDialogOpen} onOpenChange={(_, data) => setSaveLocationDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Save location to customer</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Text>
                  Enter a label for this location. It will be saved to {selectedCustomer?.name || 'the customer'}'s profile for future orders.
                </Text>
                {currentLocation && (
                  <Text style={{ color: tokens.colorNeutralForeground3, fontSize: '13px' }}>
                    Coordinates: {currentLocation.lat.toFixed(6)}, {currentLocation.lng.toFixed(6)}
                  </Text>
                )}
                <div>
                  <Label htmlFor="saveLocationLabel">Location label</Label>
                  <Input
                    id="saveLocationLabel"
                    size="large"
                    placeholder="Service Location"
                    value={saveLocationLabel}
                    onChange={(e, data) => setSaveLocationLabel(data.value)}
                    style={{ width: '100%' }}
                    autoFocus
                  />
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setSaveLocationDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  // Empty label falls back to "Service Location" inside the
                  // save handler — no friction for users who just want to
                  // drop a pin and move on.
                  const trimmed = saveLocationLabel.trim();
                  setCurrentLocationName(trimmed);
                  setSaveLocationDialogOpen(false);
                  handleSaveLocationToCustomerWithLabel(trimmed);
                }}
              >
                Save to customer
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <EmailHistoryDialog
        isOpen={emailHistoryOpen}
        onClose={() => setEmailHistoryOpen(false)}
        entries={emailHistoryEntries}
      />
    </>
  );
}
